/* assets/hrt__core.js
   Hormone Program Hub — Core JS
   FORKED from wl__core.js (2026-08-11). Shares the same data-wl-* attribute
   system, wl__core.css, and the wl-hub-portal app-proxy endpoints
   (/apps/wl-hub/bmi + /goal — both are customer-scoped, not WL-tag-gated,
   so HRT members persist weight history with zero backend changes; a
   patient in BOTH programs shares one weight log + goal, deliberately).
   HRT-specific: check-in questions (10, hormone-symptom focused), weekly
   insight library, Klaviyo metric name, no dose logging, no telemed
   compliance, WL slow-loss rate alert disabled.
   PEPTIDE_TAGS / WL_QUIZ_GOALS are mirrored from wl__core.js — when the
   anti-aging collection changes, update BOTH files.
   - Modal controller (open/close)
   - BMI calculator + weekly log (localStorage for now; upgrade path to metafields later)
   - BMR + Calorie Plan (Mifflin + activity multiplier, safety minimums)
   - Macro Targets (percent + grams), fed by BMR "Use" buttons
   - Add-ons quick-shop modal (product data, variants, add to cart)
   - Peptide selector quiz (goal-based matching against the Add-ons catalog)
*/

// -----------------------------
// Peptide quiz goal categories + editorial tagging
// (price/availability/title come live from Shopify via #wl-addons-catalog;
// this is just "which goals this product addresses" — update when new
// products are added to the anti-aging collection.)
// -----------------------------
const WL_QUIZ_GOALS = [
  { key: "immune", label: "Stay Healthy, Boost Immune System" },
  { key: "fat_loss", label: "Fat Loss / Metabolic" },
  { key: "lean_mass", label: "Muscle Gain / Lean Mass" },
  { key: "recovery", label: "Recovery / Healing" },
  // Plain-English label (Derek 2026-09-03): pain/arthritis seekers must
  // recognize this goal — routes to BPC-157 / Wolverine / GLOW.
  { key: "anti_inflammatory", label: "Pain, Inflammation & Arthritis" },
  { key: "anti_aging", label: "Anti-aging / Longevity" },
  { key: "energy", label: "Energy / Mitochondrial" },
  { key: "cognition", label: "Neurocognition / Mood" },
  { key: "skin_hair", label: "Skin / Hair" },
  { key: "hair_loss", label: "Hair Loss" },
  { key: "libido", label: "Libido / Hormonal" },
];

// dosing values below are generic, community-cited starting points (same
// style/source class as public peptide reference tools) — NOT Reflections'
// clinical protocol. Review and correct every field with your clinical team
// before relying on this in front of patients.
const PEPTIDE_TAGS = {
// ---- PREMIXED / DESIGNER LINE (2026-08-28, dosing per
// PeptideProtocol/premixed-line/DOSING-LOCKS.md; tiers tier-everyday /
// tier-premium / tier-cutting-edge are the catalog axis) ----
  "titan-blend": {
    categories: ["lean_mass", "recovery", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "15–25 units (300–500 mcg tesamorelin side)", frequency: "5 nights a week (weekends off)", administration: "Subcutaneous injection — pre-mixed, nothing to reconstitute", bestTime: "Before bed, fasted (aligns with the overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site reactions, flushing, water retention; can raise blood sugar. Start at a HALF dose for the first 3 nights." },
  },
  "serenity-blend": {
    categories: ["cognition", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "15–25 units (150–250 mcg DSIP/Selank side)", frequency: "Nightly, as needed", administration: "Subcutaneous injection — pre-mixed", bestTime: "30–60 minutes before bed", comingOff: "No taper needed", sideEffects: "Drowsiness (by design), vivid dreams, injection-site irritation. GH-axis components — start at a half dose the first 3 nights." },
  },
  "synapse-blend": {
    categories: ["cognition", "energy"], injectable: true, caution: [],
    dosing: { dose: "15–25 units (150–250 mcg Semax/Selank + 300–500 mcg MOTS-c)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or early afternoon — can be stimulating", comingOff: "No taper needed", sideEffects: "Injection-site irritation; can feel stimulating, so avoid evenings. Half dose the first 3 days." },
  },
  "focus-blend": {
    categories: ["cognition"], injectable: true, caution: [],
    dosing: { dose: "20–30 units (200–300 mcg each of Semax and Selank)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or early afternoon", comingOff: "No taper needed", sideEffects: "Generally well tolerated; can be mildly stimulating" },
  },
  "elevate-blend": {
    categories: ["recovery", "anti_inflammatory", "cognition"], injectable: true, caution: [],
    dosing: { dose: "12–20 units (240–400 mcg BPC-157/KPV side — the gut pair sets the dose)", frequency: "Daily", administration: "Subcutaneous injection — pre-mixed", bestTime: "Any time; consistency matters more than timing", comingOff: "Typically run in 4–8 week cycles", sideEffects: "Generally well tolerated; injection-site irritation" },
  },
  "eternal-blend": {
    categories: ["skin_hair", "anti_aging", "recovery"], injectable: true, caution: [],
    dosing: { dose: "12–20 units (1.2–2 mg GHK-Cu side)", frequency: "Daily", administration: "Subcutaneous injection — pre-mixed", bestTime: "Any time, consistent daily slot", comingOff: "No taper needed", sideEffects: "Injection-site irritation; copper peptides can sting on the way in" },
  },
  "euphoria-blend": {
    categories: ["energy", "libido", "skin_hair"], injectable: true, caution: [],
    dosing: { dose: "12–20 units (240–400 mcg MOTS-c side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or before exercise", comingOff: "No taper needed", sideEffects: "Injection-site irritation, mild flushing" },
  },
  "fortify-blend": {
    categories: ["immune", "recovery"], injectable: true, caution: [],
    dosing: { dose: "20–30 units (200–300 mcg Thymosin Alpha-1 side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Any time of day", comingOff: "Often run seasonally — reassess with your provider", sideEffects: "Generally well tolerated. NOT for autoimmune conditions, immunosuppressant therapy, or transplant recipients — talk to your provider first." },
  },
  "fusion-blend": {
    categories: ["fat_loss", "energy", "recovery"], injectable: true, caution: [],
    dosing: { dose: "17–33 units (0.5–1 mg tesamorelin side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning, fasted", comingOff: "Can stop without a taper", sideEffects: "Injection-site reactions, water retention; can raise blood sugar. Half dose the first 3 days." },
  },
  "ascend-blend": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "15–25 units (300–500 mcg tesamorelin + 30–50 mcg IGF-1 LR3)", frequency: "5 nights a week (weekends off)", administration: "Subcutaneous injection — pre-mixed", bestTime: "Before bed, fasted", comingOff: "Can stop without a taper", sideEffects: "Injection-site reactions, water retention, possible blood-sugar changes. Start at a HALF dose for the first 3 nights." },
  },
  "glutathione-injectable": {
    categories: ["anti_aging", "immune", "energy"], injectable: true, caution: [],
    dosing: { dose: "200–600 mg per push (100–300 units on the insulin syringe — this one is a larger draw)", frequency: "2x per week", administration: "Subcutaneous or slow IM push — pre-mixed", bestTime: "Any time of day", comingOff: "No taper needed", sideEffects: "Generally well tolerated; occasional GI upset or injection-site soreness" },
  },
  "slu-pp-332": {
    categories: ["energy", "fat_loss", "lean_mass"], injectable: true, caution: [],
    dosing: { dose: "300–500 mcg (15–25 units on the 10 mg vial)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or before exercise", comingOff: "Cycle-based — reassess monthly with your provider", sideEffects: "An exercise-mimetic research compound with limited human data — report anything unusual to your provider" },
  },
  "ss-31-elamipretide": {
    categories: ["energy", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "5–15 mg (10–30 units on the 300 mg vial)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning", comingOff: "No taper needed", sideEffects: "Injection-site reactions; generally well tolerated in trials" },
  },
  "melanotan-ii": {
    categories: ["skin_hair", "libido"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "250 mcg to start (12.5 units); 500 mcg–1 mg once established", frequency: "3x per week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Evening — the early-dose nausea passes overnight", comingOff: "The tan fades over weeks after stopping", sideEffects: "Nausea and flushing on the first injections, spontaneous erections. DARKENS EXISTING MOLES — monitor your skin and show us any change; melanocortins have case reports of atypical moles." },
  },

  "tesamorelin-ipamorelin": {
    categories: ["fat_loss", "lean_mass", "anti_aging", "recovery"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "0.5 mg tesamorelin per injection to start (0.5–1 mg typical)", frequency: "Daily", administration: "Subcutaneous injection — comes pre-mixed, nothing to reconstitute", bestTime: "Before bed, fasted (aligns with natural overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site reactions, joint aches, fluid retention; can raise blood sugar — glucose is worth monitoring" },
  },
  "thymosin-alpha-1": {
    categories: ["immune", "recovery", "anti_inflammatory", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "1.6 mg per injection", frequency: "Twice weekly; some use it daily for 5–7 days during an active illness, then stop", administration: "Subcutaneous injection — comes pre-mixed, nothing to reconstitute", bestTime: "Any time — consistency matters more than timing", comingOff: "No taper needed; often run seasonally rather than continuously", sideEffects: "Injection-site reactions; generally well tolerated. ⚠ Autoimmune conditions, immunosuppressant medication or a transplant = provider conversation first" },
  },
  // ---- 2026-08-30 WAVE (16 SKUs from the Peptide Catalog session; the old
  // sermorelin-1 + sermorelin keys were RETIRED from browsing and replaced
  // by sermorelin-injectable + sermorelin-odt. Dosing: DOSING-LOCKS.md for
  // the injectables, per-capsule/spray strengths from the variant data.
  // The needle-free entries are THE POINT — Derek: "make sure the nasal
  // sprays and the capsules are highlighted and put in the results for
  // people that don't want to give themselves injections." ----
  "sermorelin-injectable": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "200–300 mcg per injection (10 mg vial to mix, or 15 mg pre-mixed)", frequency: "5 nights a week (weekends off)", administration: "Subcutaneous injection", bestTime: "Before bed, fasted (aligns with natural overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site redness, flushing, mild headache" },
  },
  "sermorelin-odt": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 tablet (1 mg) dissolved under the tongue — don't swallow it; it absorbs through the tissue there", frequency: "5 nights a week (weekends off)", administration: "Orally disintegrating tablet — needle-free", bestTime: "Before bed, fasted (aligns with the overnight GH pulse)", comingOff: "Can stop without a taper", sideEffects: "Mild flushing or headache; generally well tolerated" },
  },
  "shred-blend": {
    categories: ["fat_loss", "lean_mass", "energy"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "15–25 units (AOD-9604 300–500 mcg / MOTS-c 300–500 mcg side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed, nothing to reconstitute", bestTime: "Morning, fasted — before exercise if you train", comingOff: "No taper needed", sideEffects: "Injection-site irritation, flushing; GH-axis components can raise blood sugar. Start at a HALF dose for the first 3 days." },
  },
  "ignite-blend": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [],
    dosing: { dose: "6–10 units (MOTS-c 300–500 mcg side — NAD+ 3–5 mg and 5-Amino-1MQ 0.3–0.5 mg ride along)", frequency: "5 days a week", administration: "Subcutaneous injection — mix the vial with 2 mL bacteriostatic water", bestTime: "Morning, fasted — before exercise if you train", comingOff: "No taper needed", sideEffects: "Injection-site irritation; NAD+ can cause brief flushing — a slower push helps. No growth-hormone components, so none of SHRED's blood-sugar or cancer-history cautions. Start at a HALF dose for the first 3 days." },
  },
  "semax": {
    categories: ["cognition", "energy"], injectable: true, caution: [],
    dosing: { dose: "300–500 mcg per injection (15–25 units)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or early afternoon — can be stimulating", comingOff: "No taper needed", sideEffects: "Injection-site irritation; avoid evenings if it keeps you up" },
  },
  "5-amino-1mq": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [],
    dosing: { dose: "500 mcg–1 mg to start (10–20 units on the 25 mg vial); up to 1.5 mg once established", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed; two vial sizes (5 mg trial / 25 mg standard)", bestTime: "Morning", comingOff: "No taper needed", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  "thymalin": {
    categories: ["immune", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "~10 mg daily (100 units) — run as a COURSE, classically about 10 days", frequency: "Daily for the length of the course, then stop", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning", comingOff: "Ending the course IS the protocol — repeat once or twice a year", sideEffects: "Injection-site irritation; on immunosuppressants or with an autoimmune condition, talk to us first" },
  },
  "5-amino-1mq-capsules": {
    categories: ["fat_loss", "energy"], injectable: false, caution: [],
    dosing: { dose: "1 capsule (50 mg)", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning, with or without food", comingOff: "No taper needed", sideEffects: "Mild GI upset possible; generally well tolerated" },
  },
  "bpc-157-tb-500-capsules": {
    categories: ["recovery", "anti_inflammatory"], injectable: false, caution: [],
    dosing: { dose: "1 capsule, 1–2x daily", frequency: "Daily", administration: "Oral capsule", bestTime: "On an empty stomach — the oral route is strongest for gut-focused use", comingOff: "Typically run 4–8 week cycles", sideEffects: "Generally well tolerated" },
  },
  "dihexa-capsules": {
    categories: ["cognition"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 capsule (5 mg)", frequency: "Daily to every other day", administration: "Oral capsule", bestTime: "Morning, with a meal containing fat", comingOff: "Cycle-based — take breaks between courses", sideEffects: "Headache or vivid dreams reported; human data is thin — this is a frontier compound" },
  },
  "dihexa-tesofensine-capsules": {
    categories: ["cognition", "fat_loss", "energy"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 capsule (5 mg dihexa / 500 mcg tesofensine)", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning — tesofensine is stimulating, avoid evenings", comingOff: "Appetite effects fade over days; no formal taper", sideEffects: "Raised heart rate and blood pressure, dry mouth, insomnia — use caution with hypertension" },
  },
  "tesofensine-capsules": {
    categories: ["fat_loss", "energy"], injectable: false, caution: [],
    dosing: { dose: "1 capsule (500 mcg)", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning — stimulating, avoid evenings", comingOff: "Appetite effects fade over days; no formal taper", sideEffects: "Raised heart rate and blood pressure, dry mouth, insomnia — use caution with hypertension" },
  },
  "slu-pp-332-bam15-capsules": {
    categories: ["fat_loss", "energy"], injectable: false, caution: [],
    dosing: { dose: "1 capsule (100 mcg SLU-PP-332 / 15 mg BAM15)", frequency: "Daily, 5 days a week", administration: "Oral capsule", bestTime: "Morning — pairs naturally with training days", comingOff: "No taper needed", sideEffects: "Warmth or flushing possible (BAM15 is a mitochondrial uncoupler); human data is thin — frontier territory" },
  },
  "selank-nasal-spray": {
    categories: ["cognition"], injectable: false, caution: [],
    dosing: { dose: "1–2 sprays per dose (2.5 mg/mL)", frequency: "1–2x daily, as needed", administration: "Intranasal spray — the route the original research used", bestTime: "Morning and/or early afternoon", comingOff: "No taper needed", sideEffects: "Brief nasal irritation; generally well tolerated" },
  },
  "semax-nasal-spray": {
    categories: ["cognition", "energy"], injectable: false, caution: [],
    dosing: { dose: "1–2 sprays per dose (2.5 mg/mL)", frequency: "1–2x daily", administration: "Intranasal spray — needle-free, ready to use", bestTime: "Morning and/or early afternoon — can be stimulating", comingOff: "No taper needed", sideEffects: "Brief nasal irritation; avoid evenings if it keeps you up" },
  },
  "methylene-blue": {
    categories: ["cognition", "energy", "anti_aging"], injectable: false, caution: ["pregnancy"],
    dosing: { dose: "1 capsule (10 mg) — low-dose is the point; at high doses the redox behaviour reverses", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning", comingOff: "No taper needed", sideEffects: "Blue-green urine is normal. Do NOT combine with SSRIs/SNRIs or MAOIs (serotonin-syndrome risk), and skip it with G6PD deficiency" },
  },
  "mk-677-ibutamoren": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 capsule (12.5 or 25 mg — start at 12.5)", frequency: "Once nightly", administration: "Oral capsule", bestTime: "Before bed — the GH pulse is overnight, and the hunger side rides through sleep", comingOff: "Can stop without a taper", sideEffects: "Water retention, increased appetite, vivid dreams; can raise blood sugar — worth watching if metabolic health is a concern" },
  },
  "tadalafil": {
    categories: ["libido"], injectable: false, caution: [],
    dosing: { dose: "1 tablet daily (5 or 10 mg — most start at 5)", frequency: "Once daily — the long half-life is the point: nothing to time around occasions", administration: "Oral tablet", bestTime: "Same time each day, with or without food", comingOff: "No taper — effects fade within a few days of stopping", sideEffects: "Headache, flushing, stuffy nose, muscle aches. NEVER combine with nitrates (dangerous blood-pressure drop) — and flag any heart condition or alpha-blocker to your provider" },
  },
  "tesamorelin": {
    categories: ["fat_loss", "anti_aging", "lean_mass"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "0.5 mg per injection to start (0.5–1 mg typical)", frequency: "Daily", administration: "Subcutaneous injection", bestTime: "Before bed, fasted (aligns with natural overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site reactions, joint aches, fluid retention; can raise blood sugar — glucose is worth monitoring" },
  },
  "enroll-in-trim-weight-loss-program-copy": {
    // requiresVisit (Derek 2026-08-30) — NOTE: on THIS portal surface the flag
    // is data-only by design: the visit banner was deliberately removed from
    // portal modals 2026-08-28 (Derek: "we've already seen them"), and the
    // public quiz section is the surface that renders it.
    categories: ["fat_loss"], injectable: true, caution: ["pregnancy"], requiresVisit: true,
    dosing: { dose: "~1 mg to start, titrated up per your provider's plan", frequency: "Once weekly", administration: "Subcutaneous injection", bestTime: "Any time of day, consistent weekly day", comingOff: "Taper down gradually to avoid rebound weight regain", sideEffects: "GI upset (nausea, constipation), increased heart rate" },
  },
  "wolverine-blend-10mg-bpc-157-10mg-tb-500-injectable-no-rx-required": {
    categories: ["recovery", "anti_inflammatory"], injectable: true, caution: [],
    dosing: { dose: "250–500 mcg BPC-157 + up to 2.5 mg TB-500 per injection", frequency: "Daily (BPC-157) / 2x weekly (TB-500)", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters more than timing", comingOff: "Typically run in 4–6 week cycles, reassess with your provider", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  "nad-patch": {
    categories: ["energy", "anti_aging"], injectable: false, caution: [],
    dosing: { dose: "1,200 mg NAD+ / 200 mg glutathione per patch", frequency: "Up to 1x weekly", administration: "Transdermal iontophoresis patch — worn overnight, needle-free", bestTime: "Apply before bed, remove in the morning", comingOff: "No taper needed", sideEffects: "Mild irritation at the application site" },
  },
  "nad-sublingual-tablets": {
    categories: ["energy", "anti_aging"], injectable: false, caution: [],
    dosing: { dose: "100 mg or 200 mg NAD+ with B12 200 mcg per tablet", frequency: "Daily", administration: "Sublingual — dissolves under the tongue, no water needed", bestTime: "Morning (may be energizing)", comingOff: "No taper needed", sideEffects: "Generally well tolerated; mild nausea possible" },
  },
  "nad-nasal-spray": {
    categories: ["energy", "anti_aging"], injectable: false, caution: [],
    dosing: { dose: "300 mg/mL — 7,500 mg per 25 mL bottle; your provider sets the number of sprays", frequency: "2–3x weekly", administration: "Intranasal spray — needle-free, the fastest of the three formats", bestTime: "Morning (may be energizing)", comingOff: "No taper needed", sideEffects: "Brief nasal irritation; mild flushing" },
  },
  "nad-injectable": {
    categories: ["energy", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "20 units (20 mg) per injection — 100 mg per week (comes pre-mixed at 100 mg/mL)", frequency: "5 days a week", administration: "Subcutaneous injection", bestTime: "Morning (may be energizing)", comingOff: "No taper needed", sideEffects: "Flushing, mild nausea — slower titration helps" },
  },
  "cjc1295-impramorelin-300-300-mcg-sublingual-2-month-supply": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "One sublingual capsule per dose", frequency: "Nightly", administration: "Sublingual — dissolves under the tongue (no injection)", bestTime: "Before bed, fasted", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Generally well tolerated; mild water retention or flushing possible" },
  },
  "glow-stack-bpc-157-tb-500-ghk-cu-the-beauty-peptide": {
    categories: ["skin_hair", "recovery", "anti_inflammatory", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "Per vial ratio on the label", frequency: "Daily to a few times weekly, per protocol", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters", comingOff: "Typically run in cycles, reassess with your provider", sideEffects: "Injection-site irritation" },
  },
  "bpc-157-peptide-capsules-pharmaceutical-grade-recovery-without-prescription-copy": {
    categories: ["recovery", "anti_inflammatory"], injectable: true, caution: [],
    dosing: { dose: "250–500 mcg per injection", frequency: "1–2x daily", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters", comingOff: "Typically run 4–8 week cycles", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  "cjc-1295-no-dac-ipamorelin-5mg-5mg": {
    categories: ["lean_mass", "recovery", "anti_aging"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "100 mcg each (CJC-1295 + Ipamorelin) per injection", frequency: "Nightly", administration: "Subcutaneous injection", bestTime: "Before bed, fasted", comingOff: "No taper required", sideEffects: "Water retention, flushing, injection-site reactions" },
  },
  // PT-141 — product handle must be exactly "pt-141" in Shopify for this
  // entry (and the quiz) to pick it up. Dosing from the Peptide Playbook
  // snapshot (same source as the mixing calculator).
  "pt-141": {
    categories: ["libido"], injectable: true, caution: ["pregnancy"],
    dosing: { dose: "1–2 mg as needed (approved 1.75 mg)", frequency: "1–2x per week MAX — PT-141 is a melanocortin like Melanotan: frequent use tans skin and darkens moles (unless that effect is wanted)", administration: "Subcutaneous injection", bestTime: "~45 minutes before activity", comingOff: "As-needed use — no cycle or taper", sideEffects: "Nausea, flushing, headache; can raise blood pressure — use caution with hypertension" },
  },
  "mots-c": {
    categories: ["fat_loss", "energy", "libido"], injectable: true, caution: [],
    dosing: { dose: "0.5–1 mg per injection to start; 3–4 mg only after a month on it and with good cardiovascular health", frequency: "2–3x weekly", administration: "Subcutaneous injection", bestTime: "Before exercise", comingOff: "No taper required", sideEffects: "Injection-site irritation, mild fatigue" },
  },
  "mega-burn-lipotropic-injection": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [], requiresVisit: true,
    dosing: { dose: "1 cc (1 mL) per injection", frequency: "Weekly", administration: "Intramuscular injection", bestTime: "Morning", comingOff: "No taper required", sideEffects: "Injection-site soreness, mild GI upset" },
  },
  "b12-vial": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [], requiresVisit: true,
    dosing: { dose: "0.25 cc (25 units on a U-100 insulin syringe)", frequency: "Weekly", administration: "Intramuscular injection", bestTime: "Morning", comingOff: "No taper required", sideEffects: "Injection-site soreness; rarely, mild flushing" },
  },
  "3-in-1-hair-restoration-formula": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "As directed on product label", frequency: "Daily", administration: "Topical", bestTime: "Per label instructions", comingOff: "No taper required", sideEffects: "Scalp irritation possible" },
  },
  "nutrafol-mens-hair-growth-supplements-clinically-tested-for-visibly-thicker-hair-and-scalp-coverage-dermatologist-recommended-1-month-supply-copy": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "4 capsules per day (per label)", frequency: "Daily", administration: "Oral", bestTime: "With a meal containing fat, for absorption", comingOff: "No taper required", sideEffects: "Generally well tolerated; mild GI upset possible" },
  },
  "glow-stack-ghk-cu-30mg-bpc-157-10mg-tb500-10mg-the-beauty-peptide-copy": {
    categories: ["skin_hair", "recovery", "anti_inflammatory", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "Per vial ratio on the label", frequency: "Daily to a few times weekly, per protocol", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters", comingOff: "Typically run in cycles, reassess with your provider", sideEffects: "Injection-site irritation" },
  },
  "nutrafol-womens-vegan-hair-growth-supplements-plant-based-for-women-18-44-years-old-clinically-tested-for-visibly-thicker-stronger-hair-dermatologist-recommended-1-month-supply-1": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "4 capsules per day (per label)", frequency: "Daily", administration: "Oral", bestTime: "With a meal containing fat, for absorption", comingOff: "No taper required", sideEffects: "Generally well tolerated; mild GI upset possible" },
  },
  "selank-10-mg-the-brain-peptide": {
    categories: ["cognition"], injectable: false, caution: [],
    dosing: { dose: "100–200 mcg per spray, 2–3 sprays per dose", frequency: "1–2x daily", administration: "Intranasal spray", bestTime: "Morning and/or early afternoon (avoid evening — can be stimulating)", comingOff: "No taper required", sideEffects: "Nasal irritation; generally well tolerated" },
  },
  "bpc-157-peptide-capsules-pharmaceutical-grade-recovery-without-prescription": {
    categories: ["recovery", "anti_inflammatory"], injectable: false, caution: [],
    dosing: { dose: "250–500 mcg per capsule", frequency: "1–2x daily", administration: "Oral capsule", bestTime: "On an empty stomach", comingOff: "Typically run 4–8 week cycles", sideEffects: "Generally well tolerated" },
  },
  "nutrafol-dht-inhibitor-md-1": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "As directed on label", frequency: "Daily", administration: "Oral", bestTime: "With food", comingOff: "No taper required", sideEffects: "Generally well tolerated" },
  },
  "testosterone-hormone-and-fertility-booster": {
    categories: ["libido"], injectable: false, caution: [], requiresVisit: true,
    dosing: { dose: "Men's and women's strengths — set by the provider at your visit", frequency: "Daily", administration: "Oral capsule", bestTime: "Same time each morning", comingOff: "Provider-managed — don't stop or restart without guidance", sideEffects: "Generally well tolerated; mood or visual changes warrant a call to the office" },
  },
  "aod-9604": {
    categories: ["fat_loss"], injectable: true, caution: [],
    dosing: { dose: "250–500 mcg per injection", frequency: "Daily", administration: "Subcutaneous injection", bestTime: "Morning, fasted", comingOff: "No taper required", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  // Epitalon — added 2026-08-11 (was live in the catalog since 8/6 with no
  // quiz entry). Dosing from the Peptide Playbook snapshot. cancer_history
  // caution is a conservative default (telomerase activator) — Derek's call
  // to relax it.
  "epitalon": {
    categories: ["anti_aging", "cognition"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "~1 mg per injection", frequency: "Daily — one vial is about a 7-week course; take a break between courses", administration: "Subcutaneous injection", bestTime: "Evening/before bed (supports sleep–wake rhythm)", comingOff: "Cycle-based — finish the course and stop; no taper needed", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
};

(function () {
  // -----------------------------
  // Tiny utilities
  // -----------------------------
  function qs(sel, root = document) { return root.querySelector(sel); }
  function qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
  function round(n) { return Math.round(n); }

  // -----------------------------
  // WL session cache (modal-to-modal)
  // -----------------------------
  window.WL = window.WL || {
    sex: "female",
    bmr: null,
    calories: { maintain: null, slow: null, fast: null, chosen: null }
  };

  // -----------------------------
  // Modal controller
  // -----------------------------
  function openModalByName(name) {
    const modal = qs(`[data-wl-modal="${name}"]`);
    if (!modal) return;

    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("wl-modal-open");

    // Modal-specific refresh hooks
    if (name === "bmi") {
      renderBmiLog();
      wlLoadBmiGraph();
    }
    if (name === "macros") {
      refreshMacroCaloriesUI();
      // Auto-advance: if a calorie target is already chosen (e.g. carried
      // over from BMR), show the macro breakdown immediately instead of
      // making them click "Calculate targets" again.
      if (window.WL && window.WL.calories && window.WL.calories.chosen) {
        wlComputeAndRenderMacros();
      }
    }
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("wl-modal-open");

    // reset per-modal status lines (nice UX)
    qsa("[data-wl-bmi-status],[data-wl-bmr-status],[data-wl-macro-status]", modal)
      .forEach(el => (el.textContent = ""));
  }

  function initModals() {
    // Open buttons
    qsa("[data-wl-open-modal]").forEach(btn => {
      btn.addEventListener("click", () => {
        const name = btn.getAttribute("data-wl-open-modal");
        if (name) openModalByName(name);
      });
    });

    // Close buttons/backdrops
    qsa("[data-wl-modal]").forEach(modal => {
      qsa("[data-wl-modal-close]", modal).forEach(btn => {
        btn.addEventListener("click", () => closeModal(modal));
      });
    });

    // ESC closes the topmost visible wl modal (simple version)
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      const open = qsa('[data-wl-modal][aria-hidden="false"]');
      if (open.length) closeModal(open[open.length - 1]);
    });
  }

  // -----------------------------
  // BMI (calculator + weekly log scaffold)
  // Storage: localStorage now. Upgrade path later: replace adapter.
  // -----------------------------
  const BMI_LOG_KEY = "wl_bmi_log_v1";

  function storageLoadBmiLog() {
    try {
      const raw = localStorage.getItem(BMI_LOG_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch (_) {
      return [];
    }
  }

  function storageSaveBmiLog(entries) {
    try {
      localStorage.setItem(BMI_LOG_KEY, JSON.stringify(entries));
    } catch (_) {
      // ignore
    }
  }

  function todayISO() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  function bmiFromImperial(weightLb, heightIn) {
    // BMI = 703 * (lb / in^2)
    if (!weightLb || !heightIn) return null;
    if (heightIn <= 0) return null;
    return 703 * (weightLb / (heightIn * heightIn));
  }

  function bmiCategory(bmi) {
    if (bmi == null || isNaN(bmi)) return "—";
    if (bmi >= 30) return "Obesity (≥ 30)";
    if (bmi >= 25) return "Overweight (25–29.9)";
    if (bmi >= 18.5) return "Normal (18.5–24.9)";
    return "Underweight (< 18.5)";
  }

  function getBmiInputs() {
    const feetEl = qs("[data-wl-bmi-feet]");
    const inchesEl = qs("[data-wl-bmi-inches]");
    const weightEl = qs("[data-wl-bmi-weight]");
    const statusEl = qs("[data-wl-bmi-status]");

    const feet = feetEl ? parseFloat(feetEl.value) : NaN;
    const inches = inchesEl ? parseFloat(inchesEl.value) : NaN;
    const weightLb = weightEl ? parseFloat(weightEl.value) : NaN;

    const heightIn = (feet * 12) + inches;

    return { feet, inches, heightIn, weightLb, statusEl };
  }

  function renderBmiResult(bmi) {
    const valueEl = qs("[data-wl-bmi-value]");
    const catEl = qs("[data-wl-bmi-category]");
    if (valueEl) valueEl.textContent = bmi == null ? "—" : (Math.round(bmi * 10) / 10).toFixed(1);
    if (catEl) catEl.textContent = bmi == null ? "—" : bmiCategory(bmi);
  }

  function renderBmiLog() {
    const listEl = qs("[data-wl-bmi-log-list]");
    if (!listEl) return;

    const entries = storageLoadBmiLog()
      .slice()
      .sort((a, b) => (a.date < b.date ? 1 : -1)); // newest first

    if (!entries.length) {
      listEl.innerHTML = `<div class="wl-help" style="margin-top:0;">No entries yet. Log once per week.</div>`;
      return;
    }

    const rows = entries.slice(0, 12).map(e => {
      const w = (typeof e.weight_lb === "number") ? e.weight_lb.toFixed(1) : "—";
      const b = (typeof e.bmi === "number") ? e.bmi.toFixed(1) : "—";
      return `
        <div style="display:flex; justify-content:space-between; gap:10px; padding:6px 0; border-bottom:1px solid #eee;">
          <div><strong>${e.date || "—"}</strong></div>
          <div>${w} lb</div>
          <div>BMI ${b}</div>
        </div>
      `;
    }).join("");

    listEl.innerHTML = rows + (entries.length > 12 ? `<div class="wl-help">Showing latest 12 entries.</div>` : "");
  }

  // -----------------------------
  // BMI trend graph (server-backed — reads the full history from the
  // customer metafield wl-hub-portal writes to, not just this browser's
  // localStorage) + "not losing at the expected rate" staff-chat prompt.
  // Expected rate (1.2-1.6 lb/week) matches the FAQ copy on the weight
  // loss enrollment page.
  // -----------------------------
  const WL_EXPECTED_MIN_WEEKLY_LOSS = 1.0; // lb/week — below this, flag it

  // Locked goal weight (set once via the check-in; staff-only reset).
  // Cached module-wide so every chart/progress render can use it.
  let wlGoalWeight = null; // { goalLb, setAt } | null
  let wlGoalKnown = false; // true once the server has answered at least once

  let wlChartMode = "weight"; // "weight" | "waist" - toggled by the pills
  let wlLastLog = null;       // last rendered log, so the toggle can re-render

  function wlRenderBmiChart(log) {
    const container = qs("[data-wl-bmi-graph-container]");
    if (!container) return;
    wlLastLog = log || [];

    // Show the Weight/Waist toggle once any waist measurement exists.
    const toggle = qs("[data-wl-chart-toggle]");
    const hasWaist = (log || []).some((e) => Number.isFinite(e.waistIn));
    if (toggle) toggle.style.display = hasWaist ? "flex" : "none";

    const series = wlChartMode === "waist"
      ? (log || []).filter((e) => Number.isFinite(e.waistIn)).map((e) => ({ date: e.date, v: e.waistIn }))
      : (log || []).map((e) => ({ date: e.date, v: e.weightLb }));

    if (series.length < 2) {
      container.innerHTML = wlChartMode === "waist"
        ? `<div class="wl-help" style="margin-top:0;">Log your waist on at least two check-ins to see this trend.</div>`
        : `<div class="wl-help" style="margin-top:0;">Log at least two weeks to see your trend.</div>`;
      return;
    }

    const width = 600, height = 180, padL = 44, padR = 16, padT = 16, padB = 28;
    const weights = series.map((e) => e.v);
    // Pull the goal into the plot range so the goal line is always visible.
    if (wlChartMode === "weight" && wlGoalWeight && Number.isFinite(wlGoalWeight.goalLb)) weights.push(wlGoalWeight.goalLb);
    const minW = Math.min(...weights);
    const maxW = Math.max(...weights);
    const range = Math.max(maxW - minW, 1); // avoid divide-by-zero on flat lines
    const padRange = range * 0.15;
    const yMin = minW - padRange;
    const yMax = maxW + padRange;

    const plotW = width - padL - padR;
    const plotH = height - padT - padB;

    const xFor = (i) => padL + (series.length === 1 ? 0 : (i / (series.length - 1)) * plotW);
    const yFor = (w) => padT + plotH - ((w - yMin) / (yMax - yMin)) * plotH;

    const points = series.map((e, i) => `${xFor(i).toFixed(1)},${yFor(e.v).toFixed(1)}`).join(" ");
    const dots = series
      .map((e, i) => `<circle cx="${xFor(i).toFixed(1)}" cy="${yFor(e.v).toFixed(1)}" r="3.5" fill="#122a44"></circle>`)
      .join("");

    const firstLabel = series[0].date;
    const lastLabel = series[series.length - 1].date;

    // Goal line — dashed brass, labeled, drawn under the trend line.
    let goalLine = "";
    if (wlChartMode === "weight" && wlGoalWeight && Number.isFinite(wlGoalWeight.goalLb)) {
      const gy = yFor(wlGoalWeight.goalLb).toFixed(1);
      goalLine =
        `<line x1="${padL}" y1="${gy}" x2="${width - padR}" y2="${gy}" stroke="#ad8952" stroke-width="1.5" stroke-dasharray="6 4"></line>` +
        `<text x="${width - padR}" y="${(gy - 5)}" font-size="11" fill="#ad8952" font-weight="bold" text-anchor="end">GOAL ${wlGoalWeight.goalLb}</text>`;
    }

    container.innerHTML = `
      <svg viewBox="0 0 ${width} ${height}" style="width:100%;height:auto;display:block;">
        <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + plotH}" stroke="#e5e7eb" stroke-width="1"></line>
        <line x1="${padL}" y1="${padT + plotH}" x2="${width - padR}" y2="${padT + plotH}" stroke="#e5e7eb" stroke-width="1"></line>
        <text x="4" y="${yFor(maxW).toFixed(1)}" font-size="11" fill="#6b7280">${maxW.toFixed(0)}</text>
        <text x="4" y="${(yFor(minW) + 4).toFixed(1)}" font-size="11" fill="#6b7280">${minW.toFixed(0)}</text>
        ${goalLine}
        <polyline points="${points}" fill="none" stroke="#122a44" stroke-width="2.5"></polyline>
        ${dots}
        <text x="${padL}" y="${height - 6}" font-size="11" fill="#6b7280">${firstLabel}</text>
        <text x="${width - padR}" y="${height - 6}" font-size="11" fill="#6b7280" text-anchor="end">${lastLabel}</text>
      </svg>
    `;
  }

  // "Inches lost" summary - first vs latest reading per body part.
  function wlRenderInchesSummary(log) {
    const el = qs("[data-wl-inches-summary]");
    if (!el) return;
    const parts = [["waistIn", "waist"], ["hipsIn", "hips"], ["armsIn", "arms"], ["legsIn", "legs"]];
    const bits = [];
    for (const [key, label] of parts) {
      const vals = (log || []).filter((e) => Number.isFinite(e[key]));
      if (vals.length >= 2) {
        const diff = Math.round((vals[0][key] - vals[vals.length - 1][key]) * 10) / 10;
        if (diff > 0) bits.push("<strong>" + label + " −" + diff + '"</strong>');
        else if (diff < 0) bits.push(label + " +" + Math.abs(diff) + '"');
        else bits.push(label + " even");
      }
    }
    if (!bits.length) { el.style.display = "none"; return; }
    el.style.display = "block";
    el.innerHTML = "📏 Since you started measuring: " + bits.join(" · ") +
      ' <span style="color:#8a8f99;">— inches tell the story even when the scale stalls.</span>';
  }

  // Weight/Waist chart toggle (pills live in the thanks screen).
  document.addEventListener("click", function (e) {
    const pill = e.target.closest("[data-wl-chart-mode]");
    if (!pill) return;
    wlChartMode = pill.getAttribute("data-wl-chart-mode") === "waist" ? "waist" : "weight";
    qsa("[data-wl-chart-mode]").forEach(function (b) {
      b.classList.toggle("is-active", b === pill);
    });
    if (wlLastLog) wlRenderBmiChart(wlLastLog);
  });

  // Confetti burst for goal-reached (Derek: "shoots off some confetti and
  // all that kind of shit"). Dependency-free canvas, brass/navy/cream,
  // fires ONCE per goal value (localStorage) so week two after the win
  // is calm; a staff reset to a new goal re-arms it. Honors
  // prefers-reduced-motion.
  const WL_GOAL_CELEBRATED_KEY = "wl_goal_celebrated_v1";

  function wlConfetti() {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:99999;";
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d");
    const colors = ["#ad8952", "#d4b483", "#122a44", "#ffffff", "#1e7a4d"];
    const parts = [];
    for (let i = 0; i < 160; i++) {
      parts.push({
        x: canvas.width / 2 + (Math.random() - 0.5) * 120,
        y: canvas.height * 0.35,
        vx: (Math.random() - 0.5) * 14,
        vy: -6 - Math.random() * 9,
        w: 5 + Math.random() * 6,
        h: 8 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        color: colors[i % colors.length]
      });
    }
    const start = Date.now();
    (function tick() {
      const t = Date.now() - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      parts.forEach(function (pt) {
        pt.x += pt.vx; pt.vy += 0.25; pt.y += pt.vy; pt.rot += pt.vr;
        ctx.save();
        ctx.translate(pt.x, pt.y);
        ctx.rotate(pt.rot);
        ctx.globalAlpha = Math.max(0, 1 - t / 3000);
        ctx.fillStyle = pt.color;
        ctx.fillRect(-pt.w / 2, -pt.h / 2, pt.w, pt.h);
        ctx.restore();
      });
      if (t < 3200) requestAnimationFrame(tick);
      else canvas.remove();
    })();
  }

  function wlMaybeCelebrateGoal(goalLb) {
    try {
      if (localStorage.getItem(WL_GOAL_CELEBRATED_KEY) === String(goalLb)) return;
      localStorage.setItem(WL_GOAL_CELEBRATED_KEY, String(goalLb));
    } catch (_) {}
    wlConfetti();
  }

  // Progress-to-goal strip: lbs down / to go / % there, plus a pace-based
  // ETA ("on pace to reach it around <date>") derived from the recent trend.
  function wlRenderGoalProgress(log) {
    const el = qs("[data-wl-goal-progress]");
    if (!el) return;
    if (!wlGoalWeight || !Number.isFinite(wlGoalWeight.goalLb) || !log || !log.length) {
      el.style.display = "none";
      return;
    }
    const goal = wlGoalWeight.goalLb;
    const start = log[0].weightLb;
    const current = log[log.length - 1].weightLb;
    const lost = Math.round((start - current) * 10) / 10;
    const toGo = Math.round((current - goal) * 10) / 10;

    let pctHtml = "";
    if (start > goal) {
      const pct = clamp(Math.round(((start - current) / (start - goal)) * 100), 0, 100);
      pctHtml =
        '<div style="height:8px;background:#eef0f4;margin:8px 0 4px;"><div style="height:100%;width:' + pct + '%;background:#ad8952;"></div></div>' +
        '<div style="font-size:15px;color:#6b7280;">' + pct + "% of the way to your goal</div>";
    }

    let headline, sub = "";
    if (toGo <= 0) {
      headline = '<span style="color:#1e7a4d;">🎉 GOAL REACHED — ' + Math.abs(toGo) + " lb past it</span>";
      sub = "Incredible. Talk to us about your maintenance plan — or lock in your next goal and keep rolling.";
      wlMaybeCelebrateGoal(goal);
    } else {
      headline = lost > 0
        ? "<strong>" + lost + " lb down</strong> · " + toGo + " lb to go"
        : toGo + " lb to your goal";
      // Pace over the recent window (up to last 5 entries, needs >= 7 days span).
      const win = log.slice(-5);
      const days = (new Date(win[win.length - 1].date) - new Date(win[0].date)) / 86400000;
      if (win.length >= 2 && days >= 7) {
        const rate = (win[0].weightLb - win[win.length - 1].weightLb) / (days / 7); // lb per week
        if (rate >= 0.15) {
          const weeks = toGo / rate;
          if (weeks <= 104) {
            const eta = new Date(Date.now() + weeks * 7 * 86400000);
            sub = "At your current pace (" + (Math.round(rate * 10) / 10) + " lb/week), you're on track to reach it around <strong>" +
              eta.toLocaleDateString(undefined, { month: "long", year: "numeric" }) + "</strong>.";
          } else {
            sub = "Keep stacking weeks — every entry sharpens this estimate.";
          }
        } else {
          sub = "The scale's been quiet lately — plateaus are normal. A few more weeks of data will put a date on this.";
        }
      } else {
        sub = "A few more weekly entries and we'll show your projected date.";
      }
    }

    el.style.display = "block";
    el.innerHTML =
      '<div style="padding:12px 14px;border:1px solid rgba(173,137,82,.45);border-left:4px solid #ad8952;background:#fbf7f1;">' +
        '<div style="font-size:15px;letter-spacing:.1em;text-transform:uppercase;color:#7a6039;font-weight:700;">Progress to goal · ' + goal + " lb</div>" +
        '<div style="font-size:16px;color:#122a44;margin-top:4px;">' + headline + "</div>" +
        pctHtml +
        (sub ? '<div style="font-size:15px;color:#4b5563;margin-top:6px;line-height:1.5;">' + sub + "</div>" : "") +
      "</div>";
  }

  function wlCheckBmiRate(log) {
    // HRT: disabled. Members may be maintaining, recomposing, or even
    // deliberately gaining muscle — the WL "losing slower than expected"
    // alert would be wrong here. Markup for the alert isn't in the HRT
    // section anyway; this early-return keeps every caller happy.
    return;
    const alertEl = qs("[data-wl-bmi-rate-alert]");
    const messageEl = qs("[data-wl-bmi-rate-message]");
    if (!alertEl || !messageEl) return;

    if (!log || log.length < 2) {
      alertEl.style.display = "none";
      return;
    }

    const latest = log[log.length - 1];
    const prior = log[log.length - 2];

    const days = (new Date(latest.date) - new Date(prior.date)) / (1000 * 60 * 60 * 24);
    if (!isFinite(days) || days <= 0) {
      alertEl.style.display = "none";
      return;
    }

    const lbLost = prior.weightLb - latest.weightLb;
    const weeklyRate = lbLost / (days / 7);

    if (weeklyRate < WL_EXPECTED_MIN_WEEKLY_LOSS) {
      const phrase = weeklyRate < 0
        ? "your weight has gone up since your last entry"
        : "you're losing slower than the typical 1.2–1.6 lb/week";
      messageEl.textContent = `Looks like ${phrase}.`;
      alertEl.style.display = "block";
    } else {
      alertEl.style.display = "none";
    }
  }

  async function wlLoadBmiGraph() {
    const statusEl = qs("[data-wl-bmi-graph-status]");
    if (statusEl) statusEl.textContent = "Loading your history…";

    try {
      const res = await fetch("/apps/wl-hub/bmi");
      const data = await res.json().catch(() => ({}));

      if (!(res.ok && data.ok)) {
        if (statusEl) statusEl.textContent = data.error || "Could not load your history.";
        wlRenderBmiChart([]);
        return;
      }

      if (statusEl) statusEl.textContent = "";
      if (data.goal !== undefined) { wlGoalWeight = data.goal; wlGoalKnown = true; }
      wlRenderBmiChart(data.log || []);
      wlCheckBmiRate(data.log || []);
      wlRenderGoalProgress(data.log || []);
      wlRenderInchesSummary(data.log || []);
    } catch (err) {
      console.error("[WL] Failed to load BMI graph:", err);
      if (statusEl) statusEl.textContent = "Could not load your history.";
    }
  }

  function initBmi() {
    const calcBtn = qs("[data-wl-bmi-calc]");
    const logBtn = qs("[data-wl-bmi-log]");
    if (!calcBtn && !logBtn) return;

    if (calcBtn) {
      calcBtn.addEventListener("click", () => {
        const { heightIn, weightLb, statusEl } = getBmiInputs();
        if (!isFinite(heightIn) || !isFinite(weightLb) || heightIn <= 0 || weightLb <= 0) {
          if (statusEl) statusEl.textContent = "Please enter a valid height and weight.";
          renderBmiResult(null);
          return;
        }
        const bmi = bmiFromImperial(weightLb, heightIn);
        renderBmiResult(bmi);
        if (statusEl) statusEl.textContent = "Calculated.";
      });
    }

    if (logBtn) {
      logBtn.addEventListener("click", async () => {
        const { heightIn, weightLb, statusEl } = getBmiInputs();
        if (!isFinite(heightIn) || !isFinite(weightLb) || heightIn <= 0 || weightLb <= 0) {
          if (statusEl) statusEl.textContent = "Please enter a valid height and weight before logging.";
          return;
        }

        const bmi = bmiFromImperial(weightLb, heightIn);
        const entry = {
          date: todayISO(),
          weight_lb: Math.round(weightLb * 10) / 10,
          bmi: Math.round(bmi * 10) / 10
        };

        const entries = storageLoadBmiLog();

        // Enforce "once per week" gently: if an entry exists today, overwrite it.
        const existingIdx = entries.findIndex(e => e.date === entry.date);
        if (existingIdx >= 0) entries[existingIdx] = entry;
        else entries.push(entry);

        storageSaveBmiLog(entries);
        renderBmiResult(bmi);
        renderBmiLog();

        if (statusEl) statusEl.textContent = "Logged for this week. (We recommend weighing once per week.)";

        // Persist server-side too (customer note + metafield log) so it's
        // visible to staff and survives a device switch, not just localStorage.
        try {
          const res = await fetch("/apps/wl-hub/bmi", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ date: entry.date, weightLb: entry.weight_lb, bmi: entry.bmi }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.ok) {
            wlRenderBmiChart(data.log || []);
            wlCheckBmiRate(data.log || []);
          } else if (statusEl) {
            statusEl.textContent = "Logged locally, but couldn't save to your profile — please try again later.";
          }
        } catch (err) {
          console.error("[WL] BMI server log failed:", err);
          if (statusEl) statusEl.textContent = "Logged locally, but couldn't save to your profile — please try again later.";
        }
      });
    }
  }

  // -----------------------------
  // BMR + Calorie Plan
  // -----------------------------
  function mifflinBmrKcal(sex, ageYears, heightCm, weightKg) {
    // Mifflin-St Jeor:
    // Male: 10W + 6.25H - 5A + 5
    // Female: 10W + 6.25H - 5A - 161
    const base = (10 * weightKg) + (6.25 * heightCm) - (5 * ageYears);
    return sex === "male" ? (base + 5) : (base - 161);
  }

  function getBmrInputs() {
    const sexEls = qsa('[data-wl-bmr-sex]');
    const ageEl = qs("[data-wl-bmr-age]");
    const feetEl = qs("[data-wl-bmr-feet]");
    const inchesEl = qs("[data-wl-bmr-inches]");
    const weightEl = qs("[data-wl-bmr-weight]");
    const actEl = qs("[data-wl-bmr-activity]");
    const statusEl = qs("[data-wl-bmr-status]");

    let sex = "female";
    sexEls.forEach(r => { if (r.checked) sex = r.value; });

    const age = ageEl ? parseFloat(ageEl.value) : NaN;
    const feet = feetEl ? parseFloat(feetEl.value) : NaN;
    const inches = inchesEl ? parseFloat(inchesEl.value) : NaN;
    const weightLb = weightEl ? parseFloat(weightEl.value) : NaN;
    const activity = actEl ? parseFloat(actEl.value) : 1.2;

    const heightIn = (feet * 12) + inches;

    return { sex, age, feet, inches, heightIn, weightLb, activity, statusEl };
  }

  function setBmrOutputs({ maintain, slow, fast }) {
    const mEl = qs("[data-wl-cal-maintain]");
    const sEl = qs("[data-wl-cal-slow]");
    const fEl = qs("[data-wl-cal-fast]");
    if (mEl) mEl.textContent = maintain != null ? String(maintain) : "—";
    if (sEl) sEl.textContent = slow != null ? String(slow) : "—";
    if (fEl) fEl.textContent = fast != null ? String(fast) : "—";
  }

  function applySafetyMinimum(sex, calories) {
    // Locked by Derek:
    // Female min 1200, Male min 1800
    const floor = (sex === "male") ? 1800 : 1200;
    return Math.max(floor, calories);
  }

  function initBmr() {
    const calcBtn = qs("[data-wl-bmr-calc]");
    if (!calcBtn) return;

    calcBtn.addEventListener("click", () => {
      const { sex, age, heightIn, weightLb, activity, statusEl } = getBmrInputs();

      if (!isFinite(age) || age < 15 || age > 90) {
        if (statusEl) statusEl.textContent = "Please enter a valid age (15–90).";
        return;
      }
      if (!isFinite(heightIn) || heightIn <= 0) {
        if (statusEl) statusEl.textContent = "Please enter a valid height.";
        return;
      }
      if (!isFinite(weightLb) || weightLb <= 0) {
        if (statusEl) statusEl.textContent = "Please enter a valid weight.";
        return;
      }

      // Convert units
      const heightCm = heightIn * 2.54;
      const weightKg = weightLb * 0.453592;

      const bmr = mifflinBmrKcal(sex, age, heightCm, weightKg);
      const tdee = bmr * (isFinite(activity) ? activity : 1.2);

      // Plans
      const maintain = applySafetyMinimum(sex, round(tdee));
      const slow = applySafetyMinimum(sex, round(tdee - 500));
      const fast = applySafetyMinimum(sex, round(tdee - 1000));

      // Save session
      window.WL.sex = sex;
      window.WL.bmr = round(bmr);
      window.WL.calories.maintain = maintain;
      window.WL.calories.slow = slow;
      window.WL.calories.fast = fast;

      setBmrOutputs({ maintain, slow, fast });

      if (statusEl) statusEl.textContent = "Plan calculated. Choose Maintain / Slow / Fast and click “Use”.";
    });

    // "Use" buttons: store chosen calories, then auto-advance straight
    // into Macro Targets with the breakdown already calculated — no
    // manual "now go open Macros yourself" step.
    qsa("[data-wl-use-calories]").forEach(btn => {
      btn.addEventListener("click", () => {
        const key = btn.getAttribute("data-wl-use-calories"); // maintain|slow|fast
        const chosen = window.WL && window.WL.calories ? window.WL.calories[key] : null;

        const statusEl = qs("[data-wl-bmr-status]");
        if (!chosen) {
          if (statusEl) statusEl.textContent = "Please calculate your plan first.";
          return;
        }

        window.WL.calories.chosen = chosen;

        const bmrModal = btn.closest("[data-wl-modal]");
        if (bmrModal) closeModal(bmrModal);
        openModalByName("macros");
      });
    });
  }

  // -----------------------------
  // Macro Targets (percent + grams)
  // -----------------------------
  const MACRO_TEMPLATES = {
    balanced:   { protein: 0.30, carbs: 0.40, fat: 0.30 },
    high_protein: { protein: 0.40, carbs: 0.30, fat: 0.30 },
    high_fat:   { protein: 0.30, carbs: 0.20, fat: 0.50 }
  };

  function refreshMacroCaloriesUI() {
    const calEl = qs("[data-wl-macro-calories]");
    if (!calEl) return;
    calEl.textContent = window.WL && window.WL.calories && window.WL.calories.chosen
      ? String(window.WL.calories.chosen)
      : "—";
  }

  function setMacroOutputs({ pPct, cPct, fPct, pG, cG, fG }) {
    const pGEl = qs("[data-wl-macro-protein-g]");
    const cGEl = qs("[data-wl-macro-carbs-g]");
    const fGEl = qs("[data-wl-macro-fat-g]");

    const pPEl = qs("[data-wl-macro-protein-pct]");
    const cPEl = qs("[data-wl-macro-carbs-pct]");
    const fPEl = qs("[data-wl-macro-fat-pct]");

    if (pGEl) pGEl.textContent = pG != null ? String(pG) : "—";
    if (cGEl) cGEl.textContent = cG != null ? String(cG) : "—";
    if (fGEl) fGEl.textContent = fG != null ? String(fG) : "—";

    if (pPEl) pPEl.textContent = pPct != null ? String(pPct) : "—";
    if (cPEl) cPEl.textContent = cPct != null ? String(cPct) : "—";
    if (fPEl) fPEl.textContent = fPct != null ? String(fPct) : "—";
  }

  // Module-scope (not nested in initMacros) so openModalByName and the BMR
  // "Use" handler can both trigger it directly — needed for auto-advance:
  // BMR -> Macros should show computed targets immediately, not require a
  // second manual "Calculate" click.
  function wlComputeAndRenderMacros(statusOverride) {
    const templateEl = qs("[data-wl-macro-template]");
    const statusEl = qs("[data-wl-macro-status]");
    const calories = window.WL && window.WL.calories ? window.WL.calories.chosen : null;

    if (!calories) {
      if (statusEl) statusEl.textContent = "Run BMR + Calorie Plan first, then click “Use” on a calorie target.";
      setMacroOutputs({ pPct: null, cPct: null, fPct: null, pG: null, cG: null, fG: null });
      refreshMacroCaloriesUI();
      return;
    }

    const key = templateEl ? (templateEl.value || "balanced") : "balanced";
    const tpl = MACRO_TEMPLATES[key] || MACRO_TEMPLATES.balanced;

    // Percent output
    const pPct = Math.round(tpl.protein * 100);
    const cPct = Math.round(tpl.carbs * 100);
    const fPct = Math.round(tpl.fat * 100);

    // Gram output:
    // Protein & carbs ~4 kcal/g; fat ~9 kcal/g
    const proteinCals = calories * tpl.protein;
    const carbsCals = calories * tpl.carbs;
    const fatCals = calories * tpl.fat;

    const pG = Math.round(proteinCals / 4);
    const cG = Math.round(carbsCals / 4);
    const fG = Math.round(fatCals / 9);

    setMacroOutputs({ pPct, cPct, fPct, pG, cG, fG });
    refreshMacroCaloriesUI();

    if (statusEl) {
      statusEl.textContent = statusOverride || "Targets calculated. Tip: enter these grams into MyFitnessPal as your daily goals.";
    }
  }

  function initMacros() {
    const calcBtn = qs("[data-wl-macro-calc]");
    const templateEl = qs("[data-wl-macro-template]");
    if (!calcBtn || !templateEl) return;

    // Button click
    calcBtn.addEventListener("click", () => wlComputeAndRenderMacros());

    // Recompute on template change if calories already chosen
    templateEl.addEventListener("change", () => {
      if (window.WL && window.WL.calories && window.WL.calories.chosen) wlComputeAndRenderMacros();
      else refreshMacroCaloriesUI();
    });

    // Keep calories display fresh
    refreshMacroCaloriesUI();
  }


  // -----------------------------
  // Dose confirmation flow (UI only for now)
  // -----------------------------
  let pendingDoseMg = null;

  function initDose() {
    const doseInput = qs("[data-wl-dose-input]");
    const saveBtn = qs("[data-wl-dose-save]");

    if (saveBtn && doseInput) {
      saveBtn.addEventListener("click", () => {
        const val = parseFloat(doseInput.value);
        const status = qs("[data-wl-dose-status]");

        if (!isFinite(val) || val <= 0) {
          if (status) status.textContent = "Please enter a valid dose in mg.";
          return;
        }

        pendingDoseMg = val;
        openModalByName("dose-confirm");
      });
    }

    const confirmBtn = qs("[data-wl-dose-confirm]");
    if (confirmBtn) {
      confirmBtn.addEventListener("click", async () => {
        if (!pendingDoseMg) return;

        const doseToSave = pendingDoseMg;
        confirmBtn.disabled = true;

        try {
          const res = await fetch("/apps/wl-hub/dose", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ dose: doseToSave }),
          });
          const data = await res.json().catch(() => ({}));

          const status = qs("[data-wl-dose-status]");
          if (res.ok && data.ok) {
            if (status) status.textContent = `Saved — dose on file: ${doseToSave} mg.`;
          } else if (status) {
            status.textContent = data.error || "Could not save your dose — please try again.";
          }
        } catch (err) {
          console.error("[WL] Dose save failed:", err);
          const status = qs("[data-wl-dose-status]");
          if (status) status.textContent = "Could not save your dose — please try again.";
        }

        confirmBtn.disabled = false;
        pendingDoseMg = null;
        closeModal(confirmBtn.closest("[data-wl-modal]"));
      });
    }
  }

  // -----------------------------
  // -----------------------------
  // Dose calculator (units -> weekly mg), ported from the post-visit
  // checkout page's inline dose calc. Assumes standard 100 units = 1 mL
  // insulin syringe, same as the source implementation.
  // -----------------------------
  let wlDosecalcResultMg = null;

  function wlDosecalcSetUseEnabled(enabled) {
    const btn = qs("[data-wl-dosecalc-use]");
    if (!btn) return;
    btn.disabled = !enabled;
    btn.style.opacity = enabled ? "1" : "0.45";
    btn.style.cursor = enabled ? "pointer" : "not-allowed";
  }

  function initDoseCalculator() {
    const computeBtn = qs("[data-wl-dosecalc-compute]");
    if (!computeBtn) return; // modal not on this page

    const concSel = qs("[data-wl-dosecalc-conc]");
    const customWrap = qs("[data-wl-dosecalc-custom-wrap]");
    if (concSel) {
      concSel.addEventListener("change", () => {
        if (customWrap) customWrap.style.display = concSel.value === "__custom__" ? "block" : "none";
      });
    }

    computeBtn.addEventListener("click", () => {
      const unitsEl = qs("[data-wl-dosecalc-units]");
      const customEl = qs("[data-wl-dosecalc-custom-conc]");
      const hintEl = qs("[data-wl-dosecalc-hint]");

      const units = unitsEl ? parseFloat(unitsEl.value) : NaN;
      const sel = concSel ? concSel.value : "";
      const conc = sel === "__custom__" ? parseFloat(customEl?.value) : parseFloat(sel);

      if (!isFinite(units) || units <= 0) {
        if (hintEl) hintEl.textContent = "Please enter a valid number of units (example: 25).";
        wlDosecalcResultMg = null;
        wlDosecalcSetUseEnabled(false);
        return;
      }
      if (!isFinite(conc) || conc <= 0) {
        if (hintEl) hintEl.textContent = "Please select (or enter) a valid concentration (mg/mL).";
        wlDosecalcResultMg = null;
        wlDosecalcSetUseEnabled(false);
        return;
      }

      const ml = units / 100;
      wlDosecalcResultMg = Math.round(ml * conc * 100) / 100;

      const mgEl = qs("[data-wl-dosecalc-result-mg]");
      // mg | mcg display toggle (Derek 2026-08-28). The LOGGED dose stays in
      // mg - this only changes what the reader sees on the result line.
      (function ensureDoseUnitToggle() {
        if (!mgEl || mgEl.getAttribute("data-unit-toggle")) return;
        mgEl.setAttribute("data-unit-toggle", "1");
        const line = mgEl.parentElement;
        const w = document.createElement("span");
        w.style.cssText = "margin-left:10px;font-size:12.5px;";
        w.innerHTML = '<button type="button" data-wl-dosecalc-unit="mg" style="border:1px solid #122a44;background:#122a44;color:#fff;padding:2px 9px;cursor:pointer;font:inherit;border-radius:0;">mg</button>' +
          '<button type="button" data-wl-dosecalc-unit="mcg" style="border:1px solid #122a44;background:#fff;color:#122a44;padding:2px 9px;cursor:pointer;font:inherit;border-radius:0;">mcg</button>';
        if (line) line.appendChild(w);
        w.addEventListener("click", function (e) {
          const b = e.target.closest("[data-wl-dosecalc-unit]");
          if (!b) return;
          const unit = b.getAttribute("data-wl-dosecalc-unit");
          w.querySelectorAll("[data-wl-dosecalc-unit]").forEach(function (x) {
            const on = x === b;
            x.style.background = on ? "#122a44" : "#fff";
            x.style.color = on ? "#fff" : "#122a44";
          });
          const mg = parseFloat(mgEl.getAttribute("data-mg") || mgEl.textContent);
          if (!isFinite(mg)) return;
          if (unit === "mcg") {
            mgEl.textContent = (Math.round(mg * 1000)).toLocaleString();
            mgEl.nextSibling && (mgEl.nextSibling.textContent = " mcg/week");
          } else {
            mgEl.textContent = mg;
            mgEl.nextSibling && (mgEl.nextSibling.textContent = " mg/week");
          }
        });
      })();
      const labelEl = qs("[data-wl-dosecalc-result-label]");
      if (mgEl) {
        // canonical mg after every compute: stamp it, show it, and snap the
        // display toggle back to mg so number and label can never disagree
        mgEl.setAttribute("data-mg", wlDosecalcResultMg.toFixed(2));
        mgEl.textContent = wlDosecalcResultMg.toFixed(2);
        if (mgEl.nextSibling) mgEl.nextSibling.textContent = " mg/week";
        document.querySelectorAll("[data-wl-dosecalc-unit]").forEach(function (x) {
          const on = x.getAttribute("data-wl-dosecalc-unit") === "mg";
          x.style.background = on ? "#122a44" : "#fff";
          x.style.color = on ? "#fff" : "#122a44";
        });
      }
      if (labelEl) labelEl.textContent = `${units} units/week @ ${conc} mg/mL`;
      if (hintEl) hintEl.textContent = "";

      wlDosecalcSetUseEnabled(true);
    });

    const useBtn = qs("[data-wl-dosecalc-use]");
    if (useBtn) {
      useBtn.addEventListener("click", () => {
        if (!wlDosecalcResultMg) return;
        // Weekly check-in's dose field is the primary target since the hero
        // dose card was folded into it (2026-08-11); the old selector is
        // kept as a fallback for any page still carrying that card.
        const doseInput = qs("[data-wl-checkin-dose]") || qs("[data-wl-dose-input]");
        if (doseInput) doseInput.value = wlDosecalcResultMg;
        closeModal(useBtn.closest("[data-wl-modal]"));
      });
    }
  }

  // -----------------------------
  // Add-ons: product quick-shop modal
  // (same pattern as the aesthetics membership lounge)
  // -----------------------------
  let wlCurrentProduct = null;
  let wlSelectedVariant = null;

  // Opens the theme's native cart drawer (if present) instead of navigating
  // to /cart, so members can keep adding add-ons without leaving the page —
  // same helper as wl-post-visit-checkout.js / the membership lounge.
  function wlTryOpenCartDrawer() {
    const openCartBtn =
      qs('[data-cart-drawer-open]') ||
      qs('[data-drawer-open="cart"]') ||
      qs('button[name="open_cart"]') ||
      qs('a[href="/cart"]') ||
      null;

    if (openCartBtn) openCartBtn.click();
  }

  // Mirror Shopify's REAL prices in the quick-shop. The discount app writes
  // the sale price and sets compare_at_price — NEVER re-discount here (that
  // double-displays the discount and mismatches the cart).
  function wlPriceHtml(cents, compareCents) {
    var price = "$" + (cents / 100).toFixed(2);
    if (!compareCents || compareCents <= cents) return price;
    var full = "$" + (compareCents / 100).toFixed(2);
    var pct = Math.round((1 - cents / compareCents) * 100);
    return '<span style="text-decoration:line-through;color:#9ca3af;font-weight:400;">' + full + "</span> " +
      '<span style="color:#7a6039;font-weight:800;">' + price + "</span> " +
      '<span style="font-size:14px;color:#1f3e63;font-weight:700;">' + pct + "% off</span>";
  }

  function wlRenderProduct(product) {
    wlCurrentProduct = product;
    wlSelectedVariant = product.variants.length === 1 ? product.variants[0].id : null;

    const mediaEl = qs("[data-wl-product-media]");
    const titleEl = qs("[data-wl-product-title]");
    const priceEl = qs("[data-wl-product-price]");
    const descEl = qs("[data-wl-product-desc]");
    const variantsEl = qs("[data-wl-product-variants]");
    const statusEl = qs("[data-wl-cart-status]");

    if (titleEl) titleEl.textContent = product.title;
    if (priceEl && product.variants[0]) {
      priceEl.innerHTML = wlPriceHtml(product.variants[0].price, product.variants[0].compare_at_price);
    }
    // full product page link — new tab so the hub session (quiz picks,
    // scroll position) stays alive behind it
    const linkEl = qs("[data-wl-product-link]");
    if (linkEl) linkEl.href = product.url || "/products/" + product.handle;

    if (descEl) {
      // Short selling blurb from the product description — strip HTML, trim.
      var raw = (product.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      if (raw.length > 240) raw = raw.slice(0, 240).replace(/\s+\S*$/, "") + "…";
      descEl.textContent = raw;
      descEl.style.display = raw ? "block" : "none";
    }
    if (statusEl) statusEl.textContent = "";

    if (mediaEl) {
      const img = product.images && product.images[0];
      mediaEl.innerHTML = img
        ? `<img src="${img}" alt="${product.title}" style="width:100%;border-radius:12px;display:block;">`
        : "";
    }

    if (variantsEl) {
      variantsEl.innerHTML = "";
      if (product.variants.length > 1) {
        product.variants.forEach((variant) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = variant.title;
          btn.className = "wl-variant-option";
          btn.style.cssText =
            "padding:6px 12px;border:1px solid #e5e7eb;border-radius:20px;background:#f5f5f5;cursor:pointer;";
          btn.addEventListener("click", () => {
            wlSelectedVariant = variant.id;
            qsa(".wl-variant-option", variantsEl).forEach((b) => (b.style.background = "#f5f5f5"));
            btn.style.background = "#dbeafe";
            if (priceEl) priceEl.innerHTML = wlPriceHtml(variant.price, variant.compare_at_price);
            // swap to the variant's own photo when it has one assigned
            if (mediaEl && variant.featured_image && variant.featured_image.src) {
              mediaEl.innerHTML =
                '<img src="' + variant.featured_image.src + '" alt="' + product.title +
                '" style="width:100%;border-radius:12px;display:block;">';
            }
          });
          variantsEl.appendChild(btn);
        });
      }
    }
  }

  // -----------------------------
  // GHL chat bridge
  // -----------------------------
  // window.leadConnector.chatWidget.openWidget() is GHL's real API for this
  // widget — confirmed via live testing 2026-07-16 (same fix already live in
  // wl-post-visit-checkout.js). Simulating a click on the widget's internal
  // shadow-DOM launcher (the old approach here) looks like it works — finds
  // a real button, clicks it, no error — but the widget silently ignores
  // untrusted synthetic clicks and never opens. Don't reintroduce that
  // fallback: because it returns `true`, the retry loop stops, permanently
  // masking the case where leadConnector just isn't ready yet.
  function wlOpenGhlChat() {
    // Toggle, not just open (2026-07-17). Capture the widget's state
    // SYNCHRONOUSLY, then act after a short delay so the triggering click
    // finishes bubbling first — GHL's own outside-click listener closes
    // the widget when a click lands outside it, which was instantly
    // undoing our open and making buttons look dead.
    var lc = window.leadConnector && window.leadConnector.chatWidget;
    if (!lc || typeof lc.openWidget !== "function") return false;
    var el = document.querySelector("chat-widget");
    var wasActive = false;
    try {
      wasActive = typeof lc.isActive === "function"
        ? !!lc.isActive()
        : !!(el && el.getAttribute("data-active") === "true");
    } catch (e) {}
    setTimeout(function () {
      try {
        if (wasActive && typeof lc.closeWidget === "function") lc.closeWidget();
        else lc.openWidget();
      } catch (e) {}
    }, 60);
    return true;
  }

  // Peptides tagged 'needs-mixing' are unusable without bacteriostatic
  // water, so a Sterile Water vial ($9) rides along automatically —
  // transparently (the status line says so) and only when the cart
  // doesn't already contain one. Variant id from /products/sterile-water.js.
  const WL_STERILE_WATER_VARIANT = 48043160830119;

  async function wlEnsureSterileWater() {
    try {
      const cart = await fetch("/cart.js").then((r) => r.json());
      if (cart.items.some((i) => i.variant_id === WL_STERILE_WATER_VARIANT)) return false;
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: WL_STERILE_WATER_VARIANT, quantity: 1 }),
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  async function wlLoadProduct(handle) {
    try {
      const product = await fetch(`/products/${handle}.js`).then((r) => r.json());
      wlRenderProduct(product);
      openModalByName("product");
    } catch (err) {
      console.error("WL Add-ons: failed to load product", err);
    }
  }

  function initAddons() {
    qsa(".wl-product-trigger").forEach((btn) => {
      btn.addEventListener("click", () => {
        const handle = btn.getAttribute("data-handle");
        if (handle) wlLoadProduct(handle);
      });
    });

    const addToCartBtn = qs("[data-wl-add-to-cart]");
    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", async () => {
        const statusEl = qs("[data-wl-cart-status]");
        if (!wlSelectedVariant) {
          if (statusEl) statusEl.textContent = "Please select an option first.";
          return;
        }

        if (statusEl) statusEl.textContent = "Adding…";
        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: wlSelectedVariant, quantity: 1 }),
          });
          if (!res.ok) {
            if (statusEl) statusEl.textContent = "Something went wrong — please try again.";
          } else {
            let msg = "Added to cart! You can keep shopping add-ons below.";
            const tags = (wlCurrentProduct && wlCurrentProduct.tags) || [];
            if (tags.indexOf("needs-mixing") > -1) {
              const waterAdded = await wlEnsureSterileWater();
              if (waterAdded) msg = "Added to cart — plus a vial of sterile water for mixing (remove it in the cart if you already have some).";
            }
            if (statusEl) statusEl.textContent = msg;
            setTimeout(wlTryOpenCartDrawer, 250);
          }
        } catch (err) {
          console.error("WL Add-ons: add to cart failed", err);
          if (statusEl) statusEl.textContent = "Something went wrong — please try again.";
        }
      });
    }
  }

  // -----------------------------
  // Peptide selector quiz
  // -----------------------------
  function wlLoadCatalog() {
    const el = qs("#wl-addons-catalog");
    if (!el) return [];
    try {
      const products = JSON.parse(el.textContent);
      // Merge live Shopify data with the editorial goal-tagging above.
      // Products with no tagging entry (e.g. Sterile Water) are dropped —
      // they're catalog items, not something the quiz should ever recommend.
      // Twin alias: graduated members' catalog carries member-twin handles
      // (`<original>-m`, 15% baked into the price) — PEPTIDE_TAGS stays keyed
      // by ORIGINAL handles, so strip the suffix for the lookup only when the
      // stripped form is a real key, and keep the twin handle on the entry so
      // the quick-shop modal loads the member-priced listing.
      const pepBase = (h) => {
        if (!h || PEPTIDE_TAGS[h]) return h;
        const b = h.replace(/-m$/, "");
        return PEPTIDE_TAGS[b] ? b : h;
      };
      return products
        .filter((p) => PEPTIDE_TAGS[pepBase(p.handle)])
        .map((p) => Object.assign({}, p, PEPTIDE_TAGS[pepBase(p.handle)], { handle: p.handle }));
    } catch (err) {
      console.error("WL Peptide Quiz: failed to parse catalog", err);
      return [];
    }
  }

  function wlRenderQuizGoalCheckboxes() {
    const container = qs("[data-wl-quiz-goals]");
    if (!container) return;
    container.innerHTML = "";
    WL_QUIZ_GOALS.forEach((goal) => {
      const label = document.createElement("label");
      label.style.cssText = "display:flex;gap:8px;align-items:center;font-size:16px;";
      label.innerHTML = `<input type="checkbox" data-wl-quiz-goal value="${goal.key}"> ${goal.label}`;
      container.appendChild(label);
    });
  }

  function wlScoreProducts(selectedGoals, noInjectables) {
    const catalog = wlLoadCatalog();
    const ranked = catalog
      .map((product) => {
        const score = product.categories.filter((c) => selectedGoals.includes(c)).length;
        return Object.assign({}, product, { score });
      })
      .filter((product) => product.score > 0)
      .filter((product) => product.available !== false)
      // SOFT RANK, not a hard filter (Derek 2026-08-30: "soft rank it, don't
      // hide the injectables") — needle-free floats to the top for avoiders,
      // injectables stay in the list below.
      .sort((a, b) => {
        if (noInjectables) {
          const an = a.injectable === false ? 0 : 1;
          const bn = b.injectable === false ? 0 : 1;
          if (an !== bn) return an - bn;
        }
        return b.score - a.score;
      });

    // Top picks by overall match — plus GUARANTEED SPECIALIST COVERAGE:
    // every goal the member checked surfaces the product that SPECIALIZES
    // in it (primary category = categories[0]), not just a generalist that
    // lists it as a side benefit. Without this, PT-141 (libido specialist)
    // never shows in batches because MOTS-C "covers" libido incidentally.
    const picks = ranked.slice(0, 4);
    const inPicks = (p) => picks.some((x) => x.handle === p.handle);
    selectedGoals.forEach((goal) => {
      const hasSpecialist = picks.some((p) => p.categories[0] === goal);
      if (!hasSpecialist) {
        const champ = ranked.find((p) => p.categories[0] === goal && !inPicks(p));
        if (champ) { picks.push(champ); return; }
      }
      if (!picks.some((p) => p.categories.includes(goal))) {
        const best = ranked.find((p) => p.categories.includes(goal) && !inPicks(p));
        if (best) picks.push(best);
      }
    });
    return picks;
  }

  function wlRenderQuizResults(matches, { pregnancy, cancer }) {
    const listEl = qs("[data-wl-quiz-results-list]");
    if (!listEl) return;

    if (matches.length === 0) {
      listEl.innerHTML = `<div class="wl-help" style="margin-top:0;">
        No specific matches for that combination — browse everything in the Add-ons section above,
        or ask your provider what they'd recommend for your goals.
      </div>`;
      return;
    }

    listEl.innerHTML = matches
      .map((product) => {
        const goalLabels = product.categories
          .map((c) => (WL_QUIZ_GOALS.find((g) => g.key === c) || {}).label)
          .filter(Boolean)
          .join(", ");

        const cautions = [];
        if (product.caution.includes("pregnancy") && pregnancy !== "no") {
          cautions.push("Please discuss this one with your provider given your pregnancy status.");
        }
        if (product.caution.includes("cancer_history") && cancer !== "no") {
          cautions.push("This acts on growth pathways — please flag your cancer history to your provider first.");
        }

        const d = product.dosing;
        const dosingRows = d
          ? `
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 14px;margin-top:10px;font-size:15px;">
              <div><strong>Dose</strong><br>${d.dose}</div>
              <div><strong>How often</strong><br>${d.frequency}</div>
              <div><strong>Administration</strong><br>${d.administration}</div>
              <div><strong>Best time</strong><br>${d.bestTime}</div>
              <div><strong>Coming off</strong><br>${d.comingOff}</div>
              <div><strong>Possible side effects</strong><br>${d.sideEffects}</div>
            </div>
          `
          : "";

        // Every variant gets its own checkbox row so members can pick
        // exactly what they want across all recommendations and add the
        // whole selection to the cart in one shot (2026-07-16, Derek).
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const variantRows = variants
          .map((v) => {
            const label =
              variants.length === 1 || v.title === "Default Title"
                ? `Add ${product.title}`
                : v.title;
            return v.available === false
              ? `<label style="display:flex;gap:8px;align-items:center;padding:7px 10px;border:1px dashed #e5e7eb;border-radius:10px;color:#9ca3af;font-size:15px;">
                   <input type="checkbox" disabled> ${label} — ${v.price} (sold out)
                 </label>`
              : `<label style="display:flex;gap:8px;align-items:center;padding:7px 10px;border:1px solid #e5e7eb;border-radius:10px;cursor:pointer;font-size:15px;">
                   <input type="checkbox" data-wl-quiz-variant="${v.id}"> <span style="flex:1;">${label}</span> <strong>${v.price}</strong>
                 </label>`;
          })
          .join("");

        return `
          <div style="border:1px solid #e5e7eb;border-radius:14px;padding:14px;">
            <div style="font-weight:700;color:#111827;"><button type="button" data-wl-quiz-info="${product.handle}" style="all:unset;cursor:pointer;font-weight:700;color:#111827;">${product.title} <span style="color:#7a6039;font-weight:700;font-size:15px;">ⓘ More info</span></button></div>
            ${product.injectable === false ? '<div style="display:inline-block;background:#eef6f0;color:#2b5c45;border:1.5px solid rgba(43,92,69,.5);font-weight:700;font-size:16px;padding:2px 10px;border-radius:2px;margin-top:4px;">💧 Needle-free</div>' : ""}
            <div style="color:#6b7280;font-size:16px;margin-top:2px;">Good for: ${goalLabels}</div>
            ${dosingRows}
            ${cautions.length ? `<div style="color:#b45309;font-size:15px;margin-top:10px;">⚠ ${cautions.join(" ")}</div>` : ""}
            ${""/* visit-required banner removed 2026-08-28 (Derek): everyone inside
                this gated portal is an established patient - "we have already
                seen them. That is why they are in there to begin with." The
                public anti-aging surface keeps its banner; only the portals skip it. */}
            <div style="display:grid;gap:6px;margin-top:12px;">${variantRows}</div>
          </div>
        `;
      })
      .join("");

    listEl.innerHTML += `
      <div style="position:sticky;bottom:0;background:#fff;padding-top:10px;">
        <button type="button" class="button button--primary" data-wl-quiz-add-selected disabled
          style="width:100%;opacity:.45;cursor:not-allowed;">
          Add selected to cart (0)
        </button>
        <div data-wl-quiz-cart-status style="color:#6b7280;font-size:15px;margin-top:8px;text-align:center;"></div>
      </div>
    `;

    const addBtn = qs("[data-wl-quiz-add-selected]", listEl);

    function refreshAddButton() {
      const count = qsa("[data-wl-quiz-variant]:checked", listEl).length;
      addBtn.textContent = `Add selected to cart (${count})`;
      addBtn.disabled = count === 0;
      addBtn.style.opacity = count === 0 ? ".45" : "1";
      addBtn.style.cursor = count === 0 ? "not-allowed" : "pointer";
    }
    listEl.addEventListener("change", (e) => {
      if (e.target.closest("[data-wl-quiz-variant]")) refreshAddButton();
    });

    listEl.addEventListener("click", (e) => {
      const infoBtn = e.target.closest("[data-wl-quiz-info]");
      if (!infoBtn) return;
      // deliberately does NOT close the quiz — the product modal stacks
      // on top (z-index bump in wl__core.css); closing it returns the
      // member to their in-progress selections.
      wlLoadProduct(infoBtn.getAttribute("data-wl-quiz-info"));
    });

    addBtn.addEventListener("click", async () => {
      const ids = qsa("[data-wl-quiz-variant]:checked", listEl).map((el) =>
        parseInt(el.getAttribute("data-wl-quiz-variant"), 10)
      );
      if (!ids.length) return;

      const statusEl = qs("[data-wl-quiz-cart-status]", listEl);
      if (statusEl) statusEl.textContent = "Adding…";
      addBtn.disabled = true;

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
        });
        if (res.ok) {
          // if anything they picked needs mixing, ride a sterile water vial along
          const cat = wlLoadCatalog();
          const needsMixing = ids.some((id) =>
            cat.some(
              (p) =>
                (p.tags || []).indexOf("needs-mixing") > -1 &&
                (p.variants || []).some((v) => v.id === id)
            )
          );
          if (needsMixing) await wlEnsureSterileWater();
          if (statusEl) statusEl.textContent = `Added ${ids.length} item${ids.length > 1 ? "s" : ""} to your cart!`;
          closeModal(addBtn.closest("[data-wl-modal]"));
          setTimeout(wlTryOpenCartDrawer, 250);
        } else {
          const err = await res.json().catch(() => ({}));
          if (statusEl) statusEl.textContent = err.description || "Something went wrong — please try again.";
          addBtn.disabled = false;
        }
      } catch (err) {
        console.error("WL Quiz: multi add to cart failed", err);
        if (statusEl) statusEl.textContent = "Something went wrong — please try again.";
        addBtn.disabled = false;
      }
    });
  }

  function initPeptideQuiz() {
    if (!qs('[data-wl-modal="peptide-quiz"]')) return;

    wlRenderQuizGoalCheckboxes();

    const submitBtn = qs("[data-wl-quiz-submit]");
    const retakeBtn = qs("[data-wl-quiz-retake]");
    const intakeEl = qs("[data-wl-quiz-intake]");
    const resultsEl = qs("[data-wl-quiz-results]");

    if (submitBtn) {
      submitBtn.addEventListener("click", () => {
        const statusEl = qs("[data-wl-quiz-status]");
        const consentEl = qs("[data-wl-quiz-consent]");

        if (!consentEl || !consentEl.checked) {
          if (statusEl) statusEl.textContent = "Please check the required box above to continue.";
          return;
        }

        const selectedGoals = qsa("[data-wl-quiz-goal]:checked").map((el) => el.value);
        if (selectedGoals.length === 0) {
          if (statusEl) statusEl.textContent = "Please select at least one goal above.";
          return;
        }

        const noInjectables = qs("[data-wl-quiz-no-injectables]")?.checked || false;
        const pregnancy = qs("[data-wl-quiz-pregnancy]")?.value || "no";
        const cancer = qs("[data-wl-quiz-cancer]")?.value || "no";

        const matches = wlScoreProducts(selectedGoals, noInjectables);
        wlRenderQuizResults(matches, { pregnancy, cancer });

        if (statusEl) statusEl.textContent = "";
        if (intakeEl) intakeEl.style.display = "none";
        if (resultsEl) resultsEl.style.display = "block";
      });
    }

    if (retakeBtn) {
      retakeBtn.addEventListener("click", () => {
        if (intakeEl) intakeEl.style.display = "block";
        if (resultsEl) resultsEl.style.display = "none";
      });
    }
  }

  // -----------------------------
  // Rivo Rewards — mirror live points/tier from the hidden Rivo Account
  // Dashboard block into our own card, and point the CTAs at the Rivo
  // "Ways to Redeem" block (visible, lower on this page) instead of a
  // dead #wl-addons anchor.
  // -----------------------------
  function wlParseRivoAccountCard(card) {
    const text = (card.textContent || "").replace(/\s+/g, " ").trim();
    const pointsMatch = text.match(/([\d,]+)\s*Points/i);
    const tierMatch = text.match(/on the\s+([A-Za-z][A-Za-z\s]*?)\s+tier/i);
    return {
      points: pointsMatch ? pointsMatch[1] : null,
      tier: tierMatch ? tierMatch[1].trim() : null,
    };
  }

  function wlSyncRivoRewards(attemptsLeft) {
    const balanceEl = qs("[data-wl-rewards-balance]");
    const tierEl = qs("[data-wl-rewards-tier]");
    const card = qs("#rivo-account-dashboard .rivo-account-card");

    if (card && (card.textContent || "").trim()) {
      const { points, tier } = wlParseRivoAccountCard(card);
      if (balanceEl && points) balanceEl.textContent = `${points} pts`;
      if (tierEl && tier) tierEl.textContent = `${tier} tier`;
      if (points) return; // got real data, stop polling
    }

    // Rivo's widget loads/fetches asynchronously — retry for a few seconds
    // before giving up and leaving the "—" placeholder.
    if (attemptsLeft > 0) {
      setTimeout(() => wlSyncRivoRewards(attemptsLeft - 1), 500);
    }
  }

  function wlScrollToRivoRewards() {
    const target =
      qs(".rivo-lp-section-title") ||
      qs(".rivo-lp-cards-container.rivo-rewards");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Fallback: Rivo block not found for some reason — don't strand the click.
      qs("#wl-addons")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function wlInsertShopAddonsButton() {
    if (qs("[data-wl-shop-addons-inline]")) return; // already inserted

    const rewardsBlock =
      qs(".rivo-lp-cards-container.rivo-rewards") ||
      qs(".rivo-lp-section-title");
    if (!rewardsBlock) return;

    // Walk up to a reasonably page-level container so the button sits
    // below the whole Rivo block rather than jammed inside its grid.
    const anchor = rewardsBlock.closest("[id^='shopify-block']") || rewardsBlock;

    const wrap = document.createElement("div");
    wrap.style.cssText = "max-width:1120px;margin:14px auto 0;padding:0 20px;text-align:center;";
    wrap.innerHTML = `
      <button type="button" data-wl-shop-addons-inline class="button button--primary">
        Shop add-ons →
      </button>
    `;
    anchor.insertAdjacentElement("afterend", wrap);

    qs("[data-wl-shop-addons-inline]", wrap).addEventListener("click", () => {
      qs("#wl-addons")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function initRivoRewards() {
    if (!qs("#rivo-account-dashboard")) return; // block not on this page

    wlSyncRivoRewards(14); // ~7s of retries

    const viewBtn = qs("[data-wl-view-rewards]");
    if (viewBtn) {
      viewBtn.addEventListener("click", (e) => {
        e.preventDefault();
        wlScrollToRivoRewards();
      });
    }

    // The Ways to Redeem block can also load async — retry insertion too.
    let insertAttempts = 14;
    (function tryInsert() {
      wlInsertShopAddonsButton();
      if (!qs("[data-wl-shop-addons-inline]") && insertAttempts > 0) {
        insertAttempts -= 1;
        setTimeout(tryInsert, 500);
      }
    })();
  }

  // -----------------------------
  // Telemed compliance popup
  //
  // Driven entirely by the daily compliance sweep
  // (rmh-credit-portal/scripts/booking-reconcile.mjs). That job is the ONLY
  // thing that decides who is out of compliance; this just surfaces its
  // verdict to the patient at login. The switch is the `needs-virtual`
  // customer tag - the same tag that enrols them in the GHL reminder ladder,
  // so the portal and the email/SMS ladder can never tell a patient two
  // different stories.
  //
  // The compliance card emits #wl-compliance-data. `state` is one of:
  //   hold    wl-grace-expired               -> shipments held now
  //   due     needs-virtual, still in grace  -> warn, nothing held yet
  //   current | exempt | none                -> no popup
  //
  // HOLD keys off wl-grace-expired, NOT needs-virtual (Derek, 2026-08-06):
  // needs-virtual is ladder ENROLMENT and gets stripped on exit/undo, so a
  // patient whose grace had genuinely expired could silently fall back to the
  // neutral card. wl-grace-expired is the durable fact.
  //
  // COPY RULE (hard, Derek 2026-08-04): never say the subscription is
  // paused/on hold/suspended. It isn't. Billing continues; only fulfilment
  // is withheld. Telling a patient their money stopped moving when it hasn't
  // is how you earn a chargeback.
  // -----------------------------
  const WL_COMPLIANCE_ACK_KEY = "wl_compliance_ack_v1";

  function wlReadComplianceData() {
    const el = document.getElementById("wl-compliance-data");
    if (!el) return null;
    let data;
    try {
      data = JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }

    // Preview override for staging: ?wl_compliance_preview=hold|due forces the
    // popup so both states can be reviewed without re-tagging a real patient.
    // Only overrides the MODAL - the card above stays server-rendered truth.
    try {
      const forced = new URLSearchParams(window.location.search).get("wl_compliance_preview");
      if (forced === "hold" || forced === "due") {
        data.state = forced;
        data.__preview = true;
      }
    } catch (err) {
      /* URLSearchParams unavailable - ignore */
    }
    return data;
  }

  // `hold` re-shows every session - a shipment-affecting notice should not be
  // permanently dismissible. `due` is a nudge, so once a day is enough.
  function wlComplianceAlreadyAcked(state) {
    try {
      if (state === "hold") {
        return sessionStorage.getItem(WL_COMPLIANCE_ACK_KEY) === "hold";
      }
      return localStorage.getItem(WL_COMPLIANCE_ACK_KEY) === new Date().toISOString().slice(0, 10);
    } catch (err) {
      return false; // storage blocked (private mode) -> show it
    }
  }

  function wlComplianceMarkAcked(state) {
    try {
      if (state === "hold") {
        sessionStorage.setItem(WL_COMPLIANCE_ACK_KEY, "hold");
      } else {
        localStorage.setItem(WL_COMPLIANCE_ACK_KEY, new Date().toISOString().slice(0, 10));
      }
    } catch (err) {
      /* storage blocked - popup just shows again, which is the safe failure */
    }
  }

  function wlComplianceCopy(d) {
    const seen = d.lastVisit
      ? "Our records show your last visit was <strong>" + d.lastVisit + "</strong>."
      : "We don’t have a telemedicine visit on record for you yet.";

    const cadence =
      "Tennessee patients need to be seen every 180 days, and out-of-state patients every 90 days.";

    if (d.state === "hold") {
      return {
        pill: "Action needed",
        pillColor: "#b3261e",
        // One title for both visit types. `wl-needs-initial` means "no virtual
        // visit on record", NOT "never received a shipment" — a patient seen
        // in-office for years carries it too, so "first shipment" was false
        // for them. "Next" is true either way. (Derek, 2026-08-06)
        title: "We need to see you before your next shipment goes out",
        body:
          '<p style="margin:0 0 10px;">' + seen + " " + cadence +
          (d.dueDate
            ? " Yours came due on <strong>" + d.dueDate + "</strong>, and the grace period has now passed."
            : "") +
          '</p><p style="margin:0;">Because of that, we have to hold your shipments until you’ve been seen. ' +
          "Book a visit and we’ll release them right away — most appointments are available within a few days.</p>",
        billing:
          "<strong>Your plan stays active and your billing continues as normal.</strong> " +
          "It’s the shipment we have to hold, not your plan — so nothing about your pricing " +
          "or your place in the program changes."
      };
    }

    // due - still inside the grace window, nothing held yet
    return {
      pill: "Visit due",
      pillColor: "#ad8952",
      title: "It’s time for your next visit",
      body:
        '<p style="margin:0 0 10px;">' + seen + " " + cadence + '</p><p style="margin:0;">' +
        (d.holdDate
          ? "Please book by <strong>" + d.holdDate + "</strong>. If we get past that date without seeing you, " +
            "we have to start holding your shipments until the visit is done."
          : "Please get a visit booked so there’s no interruption to your shipments.") +
        "</p>",
      billing:
        "If shipments do get held, <strong>your plan stays active and billing continues as normal</strong> — " +
        "it’s the shipment we hold, not your plan. Booking now avoids the whole thing."
    };
  }

  function initTelemedCompliance() {
    const data = wlReadComplianceData();
    if (!data) return;
    if (data.state !== "hold" && data.state !== "due") return;
    if (!data.__preview && wlComplianceAlreadyAcked(data.state)) return;

    const modal = qs('[data-wl-modal="telemed-compliance"]');
    if (!modal) return;

    const copy = wlComplianceCopy(data);

    const pill = qs("[data-wl-compliance-pill]", modal);
    if (pill) {
      pill.textContent = copy.pill;
      pill.style.background = copy.pillColor;
    }

    const title = qs("[data-wl-compliance-title]", modal);
    if (title) title.textContent = copy.title;

    const body = qs("[data-wl-compliance-body]", modal);
    if (body) body.innerHTML = copy.body;

    const billing = qs("[data-wl-compliance-billing]", modal);
    if (billing) {
      billing.innerHTML = copy.billing;
      billing.style.borderLeftColor = copy.pillColor;
    }

    const book = qs("[data-wl-compliance-book]", modal);
    if (book) {
      book.href = data.bookUrl;
      book.textContent = data.bookLabel;
    }

    // Mark acknowledged on any exit, including booking - if they click through
    // to book, don't nag them again on the way back.
    qsa("[data-wl-modal-close]", modal).forEach(function (btn) {
      btn.addEventListener("click", function () { wlComplianceMarkAcked(data.state); });
    });
    if (book) book.addEventListener("click", function () { wlComplianceMarkAcked(data.state); });

    // Small delay so the page (and Rivo's async blocks) settle first. Skipped
    // if the patient already opened something else.
    setTimeout(function () {
      if (qsa('[data-wl-modal][aria-hidden="false"]').length) return;
      openModalByName("telemed-compliance");
    }, 900);
  }

  // -----------------------------
  // Weekly insight (motivation / expectation-setting)
  //
  // Hand-curated explainers on why weight loss takes time — the featured
  // one rotates automatically each week (epoch-week % length, no backend),
  // and the arrows let members browse the whole library. To refresh the
  // content periodically, edit this array and push the asset. Keep the
  // pace claims consistent with the rest of the portal (1-2 lb/week).
  // -----------------------------
  const WL_MOTIVATION = [
    {
      title: "Hormone therapy is a marathon with early mile markers",
      text: "Hormone optimization works on a timeline, not a switch. Sleep and mood are usually the first things to shift — often within the first several weeks. Energy, motivation, and libido tend to follow over the next month or two. Body composition — more muscle, less fat — is the slowest chapter, typically taking three to six months of consistent therapy to show clearly.",
      takeaway: "If you're a few weeks in and only sleeping better so far, you're exactly on schedule. Log your check-ins and watch the layers arrive in order."
    },
    {
      title: "Why your labs matter as much as your symptoms",
      text: "How you feel is half the picture; what your bloodwork shows is the other half. Two people can feel identical on very different hormone levels, and some things worth tracking — red blood cell counts, estradiol balance, lipids — don't produce obvious symptoms until they're well off course. That's why your follow-up visits and lab draws aren't a formality.",
      takeaway: "Keep your quarterly visit and bring recent labs. It's how we keep your therapy both effective and safe — not just one or the other."
    },
    {
      title: "The scale can hide a body that's changing",
      text: "Optimized hormones push your body toward recomposition: building muscle while trimming fat. Muscle is denser than fat, so your weight can hold perfectly still while your waist shrinks and your strength climbs. A stalled scale during hormone therapy is often a body quietly remodeling itself.",
      takeaway: "That's why the check-in asks for tape measurements. Track your waist alongside your weight — inches tell the story the scale can't."
    },
    {
      title: "Sleep is the multiplier",
      text: "Almost every hormone we're optimizing is made, released, or regulated during sleep. Growth hormone pulses at night, testosterone production peaks during deep sleep, and cortisol resets for the morning. Short, broken sleep actively works against your therapy — while seven to nine consistent hours quietly amplifies every other thing you're doing.",
      takeaway: "Treat your bedtime like a dose. Same time, dark room, no screens at the end — it's the cheapest upgrade your protocol will ever get."
    },
    {
      title: "We treat you, not a number",
      text: "There is no single 'perfect' hormone level — the healthy range is wide, and where you feel your best inside it is individual. Chasing someone else's number, or a number you saw online, is how people end up over-treated and feeling worse. Your dose is set by your symptoms, your labs, and your response together.",
      takeaway: "Compare this month's you to last month's you — never to someone else's protocol. Your check-ins here are exactly how we see that trend."
    },
    {
      title: "Consistency beats intensity",
      text: "Stable hormone levels are what produce stable results. Doses taken on schedule keep your levels in a steady band; skipped and doubled-up doses create peaks and troughs that can feel like mood swings, energy crashes, or a therapy that 'stopped working.' The protocol working is mostly the protocol being followed.",
      takeaway: "If staying on schedule is getting hard, don't white-knuckle it — tell us in a check-in or the chat. There's usually a simpler routine we can switch you to."
    },
    {
      title: "Strength training doubles your return",
      text: "Resistance exercise and hormone optimization are partners: your therapy improves your capacity to build and keep muscle, and muscle in turn improves insulin sensitivity, metabolism, bone density, and how you feel day to day. Even two or three short sessions a week meaningfully changes what your therapy can do.",
      takeaway: "You don't need a perfect program — you need a repeatable one. Pick two days a week, lift something challenging, and let the therapy do more."
    },
    {
      title: "Expect a dip or two — don't quit inside one",
      text: "As your body adjusts and doses get fine-tuned, it's normal to have a flat week or even a step backward — especially early on, or after a dose change. These adjustment windows pass, and they're exactly the kind of thing your weekly check-in is built to catch so we can respond quickly.",
      takeaway: "A rough week is data, not a verdict. Log it honestly — that's what lets us tune your plan instead of guessing."
    },
    {
      title: "Hormones and weight work as a system",
      text: "Low testosterone, shifting estrogen, thyroid drift, and insulin resistance all make weight harder to lose — and carrying extra weight in turn suppresses and converts your hormones. Fixing one side helps the other. That's why many of our hormone patients pair their therapy with a medically supervised weight loss program: each treatment makes the other work better.",
      takeaway: "If weight has been the stubborn piece of the puzzle, look at the weight loss section below — hormone patients are often our best responders."
    }
  ];

  function initMotivation() {
    const titleEl = qs("[data-wl-insight-title]");
    const textEl = qs("[data-wl-insight-text]");
    const takeawayEl = qs("[data-wl-insight-takeaway]");
    const countEl = qs("[data-wl-insight-count]");
    const bodyEl = qs("[data-wl-insight-body]");
    if (!titleEl || !textEl) return;

    const n = WL_MOTIVATION.length;
    let idx = Math.floor(Date.now() / 604800000) % n; // rotates every 7 days

    function render() {
      const item = WL_MOTIVATION[idx];
      titleEl.textContent = item.title;
      textEl.textContent = item.text;
      takeawayEl.textContent = item.takeaway;
      if (countEl) countEl.textContent = (idx + 1) + " of " + n;
    }

    function step(delta) {
      idx = (idx + delta + n) % n;
      if (!bodyEl) { render(); return; }
      bodyEl.classList.add("is-fading");
      setTimeout(function () {
        render();
        bodyEl.classList.remove("is-fading");
      }, 150);
    }

    const prev = qs("[data-wl-insight-prev]");
    const next = qs("[data-wl-insight-next]");
    if (prev) prev.addEventListener("click", function () { step(-1); });
    if (next) next.addEventListener("click", function () { step(1); });

    render();
  }

  // -----------------------------
  // Monthly check-in (satisfaction survey -> retention alert)
  //
  // Every answer is worth 0-3 points; 8 questions, 24 max. Every completed
  // check-in fires a Klaviyo event (event + profile properties, same
  // dual-write rule as the landing-page quiz — flows read events, segments
  // read profiles). A total at or below WL_CHECKIN_ALERT_MAX, or ANY answer
  // marked flag:true, additionally POSTs the full payload to the GHL
  // inbound-webhook URL from the section setting so staff get an internal
  // notification email and can reach out before the member drops off.
  // Positive check-ins never hit the webhook.
  // -----------------------------
  const WL_CHECKIN_KEY = "hrt_checkin_last_v1"; // separate cadence from the WL portal
  const WL_CHECKIN_HEIGHT_KEY = "wl_checkin_height_v1"; // {ft,in} — asked once, prefilled after
  const WL_CHECKIN_DUE_DAYS = 7;
  // WL_CHECKIN_ALERT_MAX retired 2026-08-29 — the threshold is now derived
  // from maxScore at submit time (see alertMax) so "rather not say" is safe.
  const WL_CHECKIN_KLAVIYO_ID = "Sqyivs";       // public company_id only — same as soph-wl-landing
  const WL_CHECKIN_KLAVIYO_REV = "2024-02-15";
  const WL_CHECKIN_METRIC = "HRT Weekly Check-in Completed";

  const WL_CHECKIN_QUESTIONS = [
    { k: "overall", t: "Overall, how are you feeling about your hormone program?",
      opts: [
        { label: "Great — really happy with it", pts: 3 },
        { label: "Good", pts: 2 },
        { label: "Just okay", pts: 1 },
        { label: "Not good", pts: 0, flag: "unhappy with program" }
      ] },
    { k: "energy", t: "How has your energy been this week?",
      opts: [
        { label: "Strong and steady", pts: 3 },
        { label: "Better than before I started", pts: 2 },
        { label: "About the same as before treatment", pts: 1 },
        { label: "Worse — running on empty", pts: 0, flag: "energy worse than baseline" }
      ] },
    { k: "sleep", t: "How are you sleeping?",
      opts: [
        { label: "Deep and consistent", pts: 3 },
        { label: "Pretty well most nights", pts: 2 },
        { label: "Restless, or waking during the night", pts: 1 },
        { label: "Poorly — it's affecting my days", pts: 0 }
      ] },
    { k: "mood", t: "Mood and stress this week?",
      opts: [
        { label: "Even-keeled and positive", pts: 3 },
        { label: "Mostly good", pts: 2 },
        { label: "Up and down", pts: 1 },
        { label: "Irritable, low, or anxious most days", pts: 0, flag: "mood concerns" }
      ] },
    { k: "libido", t: "Sex drive and desire this week?",
      opts: [
        { label: "Strong — where I want it", pts: 3 },
        { label: "Improving", pts: 2 },
        { label: "Still lower than I'd like", pts: 1, flag: "low sex drive" },
        { label: "Low, or worse than before I started", pts: 0, flag: "low sex drive" }
      ] },
    // Sexual function is asked separately from desire (Derek 2026-08-29): they
    // are different problems with different treatments, and a patient can have
    // plenty of drive and still have a function complaint. Either one scoring
    // low raises a flag, which is what lets Derek follow up with the right
    // medication. "Rather not say" carries skip:true — it is excluded from BOTH
    // the score and the max, so declining a personal question can never look
    // like a bad week.
    { k: "sexual_function", t: "Sexual function and satisfaction?",
      opts: [
        { label: "Good — no complaints", pts: 3 },
        { label: "Improving", pts: 2 },
        { label: "Hit or miss", pts: 1, flag: "sexual function concern" },
        { label: "A real problem for me", pts: 0, flag: "sexual function concern" },
        { label: "I'd rather not say", pts: 0, skip: true }
      ] },
    { k: "clarity", t: "Mental clarity and focus?",
      opts: [
        { label: "Sharp", pts: 3 },
        { label: "Improving", pts: 2 },
        { label: "Still foggy at times", pts: 1 },
        { label: "Brain fog is a real problem", pts: 0 }
      ] },
    { k: "symptoms", t: "The symptoms that brought you to hormone therapy — how controlled are they?",
      opts: [
        { label: "Almost completely controlled", pts: 3 },
        { label: "Noticeably better", pts: 2 },
        { label: "Only slightly better", pts: 1 },
        { label: "Not controlled at all", pts: 0, flag: "symptoms uncontrolled" }
      ] },
    { k: "side_effects", t: "Any side effects from your therapy?",
      opts: [
        { label: "None to speak of", pts: 3 },
        { label: "Mild and manageable", pts: 2 },
        { label: "Bothersome", pts: 1 },
        { label: "Rough — they affect my daily life", pts: 0, flag: "significant side effects" }
      ] },
    { k: "headaches", t: "Headaches this week?",
      opts: [
        { label: "None", pts: 3 },
        { label: "One or two, mild", pts: 2 },
        { label: "Frequent, or hard to shake", pts: 1, flag: "frequent headaches" },
        { label: "Severe, or with vision changes", pts: 0, flag: "SEVERE HEADACHE / VISION CHANGE" }
      ] },
    { k: "adherence", t: "Are you staying on schedule with your medications?",
      opts: [
        { label: "Like clockwork", pts: 3 },
        { label: "Most of the time", pts: 2 },
        { label: "I've missed a couple lately", pts: 1 },
        { label: "I'm struggling to stay on track", pts: 0, flag: "adherence struggling" }
      ] },
    { k: "continue", t: "How likely are you to continue your hormone program?",
      opts: [
        { label: "Definitely continuing", pts: 3 },
        { label: "Probably", pts: 2 },
        { label: "Not sure yet", pts: 1, flag: "unsure about continuing" },
        { label: "I'm thinking about stopping", pts: 0, flag: "considering stopping" }
      ] }
  ];

  // Sex-specific questions (Derek 2026-08-31, from his trainer's walkthrough:
  // "we should ask specific questions for men and for women"). Appended to the
  // shared set once we know the patient's sex; two each, to keep the check-in
  // near two minutes.
  //
  // These are the complaints that are genuinely sex-specific ON THERAPY, not
  // just generally different: men get the estrogen-conversion and sleep-apnea
  // signals, women get vasomotor symptoms and — importantly — unexpected
  // bleeding, which always warrants a look.
  const HRT_SEX_QUESTIONS = {
    male: [
      { k: "male_chest", t: "Any breast tenderness, puffiness, or swelling?",
        opts: [
          { label: "None", pts: 3 },
          { label: "Slight — barely notice it", pts: 2 },
          { label: "Noticeable", pts: 1, flag: "possible estrogen conversion (chest tenderness)" },
          { label: "Definitely — it's bothering me", pts: 0, flag: "possible estrogen conversion (chest tenderness)" }
        ] },
      { k: "male_sleep_breathing", t: "Snoring or waking up gasping — any worse lately?",
        opts: [
          { label: "No change", pts: 3 },
          { label: "A little more snoring", pts: 2 },
          { label: "Noticeably worse", pts: 1, flag: "worsening snoring / possible sleep apnea" },
          { label: "Waking up gasping or choking", pts: 0, flag: "worsening snoring / possible sleep apnea" }
        ] }
    ],
    female: [
      { k: "female_vasomotor", t: "Hot flashes or night sweats this week?",
        opts: [
          { label: "None at all", pts: 3 },
          { label: "A few, easy to live with", pts: 2 },
          { label: "Most days", pts: 1 },
          { label: "Constant — they're disrupting my life", pts: 0, flag: "uncontrolled hot flashes / night sweats" }
        ] },
      { k: "female_bleeding", t: "Any unexpected spotting or bleeding?",
        opts: [
          { label: "None", pts: 3 },
          { label: "Light spotting once", pts: 1, flag: "unexpected vaginal bleeding" },
          { label: "More than once this week", pts: 0, flag: "unexpected vaginal bleeding" },
          { label: "Not applicable for me", pts: 0, skip: true }
        ] }
    ]
  };

  // Filled from /apps/wl-hub/hrt when the check-in opens.
  let hrtProfile = { sex: null, testMg: null, route: null, loaded: false };

  function hrtActiveQuestions() {
    const extra = (hrtProfile.sex && HRT_SEX_QUESTIONS[hrtProfile.sex]) || [];
    return WL_CHECKIN_QUESTIONS.concat(extra);
  }

  // Blood-pressure evaluation. Two independent signals:
  //   ABSOLUTE — a reading high enough to matter on its own.
  //   TREND    — the point of collecting it weekly. On testosterone a rising BP
  //              is an early proxy for climbing red cell mass, and a patient can
  //              drift from 118 to 138 without ever crossing a "high" threshold.
  // Thresholds follow the usual stage-2 (140/90) and crisis (180/120) lines.
  // This NEVER diagnoses and never tells anyone to change a dose — it raises a
  // flag for staff, except for a crisis reading, where the only safe portal
  // behaviour is "get seen now".
  function hrtEvaluateBp(sys, dia, priorLog) {
    const out = { flags: [], urgent: false, note: "" };
    if (!Number.isFinite(sys) || !Number.isFinite(dia)) return out;

    if (sys >= 180 || dia >= 120) {
      out.urgent = true;
      out.flags.push("BLOOD PRESSURE CRISIS READING " + sys + "/" + dia);
      out.note = "That blood pressure reading is high enough that it should be checked today.";
      return out;
    }
    if (sys >= 140 || dia >= 90) {
      out.flags.push("elevated blood pressure " + sys + "/" + dia);
      out.note = "Your blood pressure is running higher than we like to see.";
    }

    // Trend: compare against the average of up to 4 prior readings. Needs at
    // least 2 priors so one odd cuff reading can't manufacture a trend.
    const priors = (priorLog || [])
      .filter(function (e) { return Number.isFinite(e.bpSys); })
      .slice(-5, -1);
    if (priors.length >= 2) {
      const avgSys = priors.reduce(function (a, e) { return a + e.bpSys; }, 0) / priors.length;
      const avgDia = priors.reduce(function (a, e) { return a + e.bpDia; }, 0) / priors.length;
      if (sys - avgSys >= 12 || dia - avgDia >= 8) {
        out.flags.push("blood pressure climbing (" + Math.round(avgSys) + "/" + Math.round(avgDia) +
                       " -> " + sys + "/" + dia + ")");
        if (!out.note) {
          out.note = "Your blood pressure has been climbing compared with your last few check-ins.";
        }
      }
    }
    return out;
  }

  function wlCheckinConfig() {
    try {
      const el = document.getElementById("wl-checkin-config");
      return el ? JSON.parse(el.textContent) : null;
    } catch (_) { return null; }
  }

  function wlCheckinLastDone() {
    try {
      const raw = localStorage.getItem(WL_CHECKIN_KEY);
      if (!raw) return null;
      const d = new Date(raw);
      return isNaN(d) ? null : d;
    } catch (_) { return null; }
  }

  function wlCheckinRenderCardState() {
    const badge = qs("[data-wl-checkin-due]");
    const lastLine = qs("[data-wl-checkin-lastdone]");
    const last = wlCheckinLastDone();
    const due = !last || ((Date.now() - last.getTime()) / 86400000) >= WL_CHECKIN_DUE_DAYS;
    if (badge) badge.style.display = due ? "inline-block" : "none";
    if (lastLine && last) {
      lastLine.textContent = "Last check-in: " + last.toLocaleDateString(undefined, { month: "long", day: "numeric" }) + (due ? " — time for another." : ". You're all set for this week.");
      lastLine.style.display = "block";
    }
  }

  function wlCheckinKlaviyo(email, props) {
    // Dual write: event properties for flows, profile properties for
    // segments/campaigns. Never allowed to block or fail the check-in.
    return fetch("https://a.klaviyo.com/client/events/?company_id=" + WL_CHECKIN_KLAVIYO_ID, {
      method: "POST",
      headers: { "Content-Type": "application/json", "revision": WL_CHECKIN_KLAVIYO_REV },
      body: JSON.stringify({ data: { type: "event", attributes: {
        properties: props,
        metric: { data: { type: "metric", attributes: { name: WL_CHECKIN_METRIC } } },
        profile: { data: { type: "profile", attributes: { email: email, properties: {
          "Last HRT Check-in": props["Completed At"],
          "Last HRT Check-in Score": props["Score"],
          "Last HRT Check-in Percent": props["Score Percent"],
          "Last HRT Check-in Flagged": props["Flagged"]
        } } } }
      } } })
    }).catch(function () {});
  }

  function wlCheckinAlert(webhook, payload) {
    if (!webhook || webhook.indexOf("http") !== 0 || webhook.indexOf("example.com") !== -1) {
      console.warn("[WL] Check-in alert webhook not configured — low score NOT forwarded.", payload);
      return Promise.resolve();
    }
    return fetch(webhook, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    }).catch(function (err) { console.error("[WL] Check-in alert webhook failed:", err); });
  }

  function initCheckin() {
    const modal = qs('[data-wl-modal="checkin"]');
    if (!modal) return;

    wlCheckinRenderCardState();

    const cfg = wlCheckinConfig() || {};
    const customer = cfg.customer || {};

    const surveyEl = qs("[data-wl-checkin-survey]", modal);
    const thanksEl = qs("[data-wl-checkin-thanks]", modal);
    const stepEl = qs("[data-wl-checkin-step]", modal);
    const titleEl = qs("[data-wl-checkin-title]", modal);
    const optsEl = qs("[data-wl-checkin-options]", modal);
    const fillEl = qs("[data-wl-checkin-fill]", modal);
    const backBtn = qs("[data-wl-checkin-back]", modal);
    const numbersWrap = qs("[data-wl-checkin-numbers]", modal);
    const weightEl = qs("[data-wl-checkin-weight]", modal);
    const feetEl = qs("[data-wl-checkin-feet]", modal);
    const inchesEl = qs("[data-wl-checkin-inches]", modal);
    const doseEl = qs("[data-wl-checkin-dose]", modal);
    const waistEl = qs("[data-wl-checkin-waist]", modal);
    const hipsEl = qs("[data-wl-checkin-hips]", modal);
    const armsEl = qs("[data-wl-checkin-arms]", modal);
    const legsEl = qs("[data-wl-checkin-legs]", modal);
    const sexWrap = qs("[data-hrt-sex-wrap]", modal);
    const bpSysEl = qs("[data-hrt-bp-sys]", modal);
    const bpDiaEl = qs("[data-hrt-bp-dia]", modal);
    const doseMgEl = qs("[data-hrt-dose-mg]", modal);
    const goalWrap = qs("[data-wl-checkin-goal-wrap]", modal);
    const goalEl = qs("[data-wl-checkin-goal]", modal);
    const goalLockEl = qs("[data-wl-checkin-goal-lock]", modal);
    const numbersErr = qs("[data-wl-checkin-numbers-err]", modal);
    const numbersNext = qs("[data-wl-checkin-numbers-next]", modal);
    const commentWrap = qs("[data-wl-checkin-comment-wrap]", modal);
    const commentEl = qs("[data-wl-checkin-comment]", modal);
    const submitBtn = qs("[data-wl-checkin-submit]", modal);
    const statusEl = qs("[data-wl-checkin-status]", modal);

    // Step 0 = weigh-in + dose ("your numbers"), then the questions, then
    // the free-text comment. numbers = {weightLb, bmi, doseMg} once step 0
    // validates.
    // Recomputed per open: the question count changes once we know sex.
    let activeQuestions = hrtActiveQuestions();
    let TOTAL_STEPS = activeQuestions.length + 2;
    let step = 0;
    let answers = {};
    let numbers = null;
    let submitting = false;
    let doseWarnedFor = null; // high-dose warning: remembers the value already warned about
    let pendingGoalLb = null; // goal typed + lock-checked this session, saved on submit
    let pendingSex = null;    // chosen in the one-time sex prompt
    let pendingRoute = null;  // SC/IM chosen this session

    // Ask the server whether a goal is on file; the set-once block only shows
    // when there isn't. Non-blocking — if it hasn't answered by the numbers
    // step, the block just stays hidden and we ask again next week.
    function refreshGoalPrompt() {
      fetch("/apps/wl-hub/goal")
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (!data.ok) return;
          wlGoalWeight = data.goal || null;
          wlGoalKnown = true;
          if (goalWrap) goalWrap.style.display = wlGoalWeight ? "none" : "block";
          const lockedLine = qs("[data-wl-checkin-goal-locked]", modal);
          const lockedVal = qs("[data-wl-checkin-goal-locked-value]", modal);
          if (lockedLine) lockedLine.style.display = wlGoalWeight ? "block" : "none";
          if (lockedVal && wlGoalWeight) lockedVal.textContent = wlGoalWeight.goalLb + " lb";
        })
        .catch(function () {});
    }

    function prefillHeight() {
      try {
        const h = JSON.parse(localStorage.getItem(WL_CHECKIN_HEIGHT_KEY) || "null");
        if (h && feetEl && !feetEl.value) feetEl.value = h.ft;
        if (h && inchesEl && !inchesEl.value) inchesEl.value = h.in;
      } catch (_) {}
    }

    function render() {
      surveyEl.style.display = "block";
      thanksEl.style.display = "none";
      if (fillEl) fillEl.style.width = Math.round((step / TOTAL_STEPS) * 100) + "%";
      if (backBtn) backBtn.style.visibility = step > 0 ? "visible" : "hidden";
      if (statusEl) statusEl.textContent = "";

      const onNumbers = step === 0;
      const onComment = step >= TOTAL_STEPS - 1;
      if (numbersWrap) numbersWrap.style.display = onNumbers ? "block" : "none";
      commentWrap.style.display = onComment ? "block" : "none";
      optsEl.style.display = (onNumbers || onComment) ? "none" : "flex";

      if (onNumbers) {
        if (stepEl) stepEl.textContent = "First, your numbers";
        if (titleEl) titleEl.textContent = "This week's weigh-in";
        if (numbersErr) numbersErr.textContent = "";
        prefillHeight();
        return;
      }

      if (onComment) {
        if (stepEl) stepEl.textContent = "Last one";
        if (titleEl) titleEl.textContent = "Anything you'd like us to know?";
        return;
      }

      const qIdx = step - 1;
      const q = activeQuestions[qIdx];
      if (stepEl) stepEl.textContent = "Question " + (qIdx + 1) + " of " + activeQuestions.length;
      if (titleEl) titleEl.textContent = q.t;
      optsEl.innerHTML = "";
      q.opts.forEach(function (opt, idx) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "wl-checkin-pill" + (answers[q.k] === idx ? " is-selected" : "");
        btn.textContent = opt.label;
        btn.addEventListener("click", function () {
          answers[q.k] = idx;
          step += 1;
          render();
        });
        optsEl.appendChild(btn);
      });
    }

    if (numbersNext) numbersNext.addEventListener("click", function () {
      const weightLb = weightEl ? parseFloat(weightEl.value) : NaN;
      const ft = feetEl ? parseFloat(feetEl.value) : NaN;
      const inch = inchesEl ? parseFloat(inchesEl.value) : 0;
      const doseRaw = doseEl ? doseEl.value.trim() : "";
      const doseMg = doseRaw === "" ? null : parseFloat(doseRaw);

      if (!isFinite(weightLb) || weightLb < 60 || weightLb > 800) {
        if (numbersErr) numbersErr.textContent = "Please enter a valid weight in pounds.";
        return;
      }
      const heightIn = (isFinite(ft) ? ft : 0) * 12 + (isFinite(inch) ? inch : 0);
      if (!isFinite(heightIn) || heightIn < 36 || heightIn > 96) {
        if (numbersErr) numbersErr.textContent = "Please enter a valid height (feet and inches).";
        return;
      }
      if (doseRaw !== "" && (!isFinite(doseMg) || doseMg <= 0)) {
        if (numbersErr) numbersErr.textContent = "Please enter your dose in mg — or use the converter if you only know units.";
        return;
      }
      // A dose typed in units by mistake is the classic error this flow
      // replaced the confirm-modal for. Derek 2026-08-11: anything over
      // 15 mg (the top weekly tirzepatide dose) prompts a warning — a
      // second Continue click accepts the value, so a genuine outlier
      // isn't hard-blocked. Changing the number re-arms the warning.
      if (doseMg !== null && doseMg > 15 && doseWarnedFor !== doseMg) {
        doseWarnedFor = doseMg;
        if (numbersErr) numbersErr.textContent = "That's higher than any usual weekly dose — doses over 15 mg are almost always units, not mg. Double-check it (the converter link below can help), or press Continue again if " + doseMg + " mg is really correct.";
        return;
      }

      // Sex must be answered before we can ask the right questions.
      if (sexWrap && sexWrap.style.display !== "none" && !pendingSex) {
        if (numbersErr) numbersErr.textContent = "Please tap Male or Female above — we only ask this once.";
        return;
      }

      // Blood pressure: both numbers or neither. A lone reading is unreadable
      // and would poison the trend line, so we refuse the half-pair rather than
      // silently dropping it.
      const bpSysRaw = bpSysEl ? bpSysEl.value.trim() : "";
      const bpDiaRaw = bpDiaEl ? bpDiaEl.value.trim() : "";
      let bpSys = null, bpDia = null;
      if (bpSysRaw !== "" || bpDiaRaw !== "") {
        bpSys = parseFloat(bpSysRaw); bpDia = parseFloat(bpDiaRaw);
        if (!isFinite(bpSys) || !isFinite(bpDia)) {
          if (numbersErr) numbersErr.textContent = "Please enter BOTH blood pressure numbers, or leave both blank.";
          return;
        }
        if (bpSys < 70 || bpSys > 260 || bpDia < 40 || bpDia > 160 || bpSys <= bpDia) {
          if (numbersErr) numbersErr.textContent = "That blood pressure doesn't look right — top number over the bottom, e.g. 124 / 78.";
          return;
        }
      }

      // Testosterone dose (mg/week). Same class of mistake as the WL portal's
      // units-vs-mg slip, so the ceiling is a hard reject rather than a warning.
      const doseRaw2 = doseMgEl ? doseMgEl.value.trim() : "";
      let testMg = null;
      if (doseRaw2 !== "") {
        testMg = parseFloat(doseRaw2);
        if (!isFinite(testMg) || testMg <= 0 || testMg > 600) {
          if (numbersErr) numbersErr.textContent = "Please enter your weekly testosterone dose in mg (for example 100).";
          return;
        }
      }

      // Optional tape measurements (inches) - independent, validated only if present.
      const measurements = {};
      const tapeFields = [[waistEl, "waistIn", "waist"], [hipsEl, "hipsIn", "hips"], [armsEl, "armsIn", "arms"], [legsEl, "legsIn", "legs"]];
      for (const pair of tapeFields) {
        const el = pair[0], key = pair[1], label = pair[2];
        if (!el || el.value.trim() === "") continue;
        const v = parseFloat(el.value);
        if (!isFinite(v) || v < 5 || v > 120) {
          if (numbersErr) numbersErr.textContent = "That " + label + " measurement doesn't look right — inches, please (or leave it blank).";
          return;
        }
        measurements[key] = Math.round(v * 10) / 10;
      }

      // Goal weight (optional, set-once): a typed goal needs the lock-in box.
      pendingGoalLb = null;
      if (goalWrap && goalWrap.style.display !== "none" && goalEl && goalEl.value.trim() !== "") {
        const goalLb = parseFloat(goalEl.value);
        if (!isFinite(goalLb) || goalLb < 60 || goalLb > 800) {
          if (numbersErr) numbersErr.textContent = "Please enter a realistic goal weight in pounds (or leave it blank for now).";
          return;
        }
        if (goalLb >= weightLb) {
          if (numbersErr) numbersErr.textContent = "Your goal should be below your current weight — double-check the number.";
          return;
        }
        if (!goalLockEl || !goalLockEl.checked) {
          if (numbersErr) numbersErr.textContent = "Check the “lock it in” box to confirm your goal — or clear it to decide later.";
          return;
        }
        pendingGoalLb = Math.round(goalLb * 10) / 10;
      }

      const bmi = Math.round(bmiFromImperial(weightLb, heightIn) * 10) / 10;
      if (bpSys !== null) { measurements.bpSys = Math.round(bpSys); measurements.bpDia = Math.round(bpDia); }
      if (testMg !== null) measurements.testMg = Math.round(testMg * 10) / 10;
      if (pendingRoute) measurements.route = pendingRoute;

      numbers = { weightLb: Math.round(weightLb * 10) / 10, heightIn: heightIn, bmi: bmi, doseMg: doseMg,
        bpSys: bpSys === null ? null : Math.round(bpSys),
        bpDia: bpDia === null ? null : Math.round(bpDia),
        testMg: testMg === null ? null : Math.round(testMg * 10) / 10,
        route: pendingRoute || null,
        measurements: Object.keys(measurements).length ? measurements : null };
      try { localStorage.setItem(WL_CHECKIN_HEIGHT_KEY, JSON.stringify({ ft: ft, in: isFinite(inch) ? inch : 0 })); } catch (_) {}
      step = 1;
      render();
    });

    function reset() {
      step = 0;
      answers = {};
      numbers = null;
      submitting = false;
      doseWarnedFor = null;
      pendingGoalLb = null;
      if (weightEl) weightEl.value = "";
      if (doseEl) doseEl.value = "";
      if (commentEl) commentEl.value = "";
      if (goalEl) goalEl.value = "";
      if (goalLockEl) goalLockEl.checked = false;
      [waistEl, hipsEl, armsEl, legsEl].forEach(function (el) { if (el) el.value = ""; });
      pendingSex = null;
      if (bpSysEl) bpSysEl.value = "";
      if (bpDiaEl) bpDiaEl.value = "";
      if (doseMgEl) doseMgEl.value = "";
      if (sexWrap) sexWrap.style.display = "none";
      qsa("[data-hrt-sex-choice]", modal).forEach(function (b) { b.classList.remove("is-selected"); });
      if (goalWrap) goalWrap.style.display = "none";
      refreshGoalPrompt();
      refreshHrtProfile();
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = "Finish my check-in"; }
      render();
    }

    // One-time sex prompt + SC/IM route: both are pill pickers, so selection is
    // just a class swap plus remembering the value for submit.
    function hrtPickPill(attr, value) {
      qsa("[" + attr + "]", modal).forEach(function (b) {
        b.classList.toggle("is-selected", b.getAttribute(attr) === value);
      });
    }
    qsa("[data-hrt-sex-choice]", modal).forEach(function (btn) {
      btn.addEventListener("click", function () {
        pendingSex = btn.getAttribute("data-hrt-sex-choice");
        hrtPickPill("data-hrt-sex-choice", pendingSex);
      });
    });
    qsa("[data-hrt-route-choice]", modal).forEach(function (btn) {
      btn.addEventListener("click", function () {
        pendingRoute = btn.getAttribute("data-hrt-route-choice");
        hrtPickPill("data-hrt-route-choice", pendingRoute);
      });
    });

    // Pull sex/dose/route so the question set and prefills are right. Runs on
    // every open; cheap, and it picks up a staff correction immediately.
    function refreshHrtProfile() {
      return fetch("/apps/wl-hub/hrt")
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (!data.ok) return;
          hrtProfile = { sex: data.sex || null, testMg: data.testMg, route: data.route, loaded: true };
          // Intake ack lives on the customer now — adopt it and mirror locally,
          // so a patient who acked on another device is never asked again.
          if (data.intakeAck) {
            hrtIntakeAckServer = data.intakeAck;
            try { localStorage.setItem(HRT_INTAKE_KEY, "1"); } catch (_) {}
            const icard = qs("[data-hrt-intake-card]");
            if (icard) icard.style.display = "none";
            hrtHideIntakeGate();
          }
          // Asking is the ONLY way to get sex-specific questions, so the prompt
          // shows whenever the server has nothing on file.
          if (sexWrap) sexWrap.style.display = hrtProfile.sex ? "none" : "block";
          if (hrtProfile.route) { pendingRoute = hrtProfile.route; hrtPickPill("data-hrt-route-choice", hrtProfile.route); }
          // Derek 2026-08-31: "make it ask for the milligram dose weekly." Prefill
          // the actual VALUE (not just a placeholder) so confirming the week takes
          // one tap and we get a real datapoint every check-in instead of only on
          // the weeks a dose happened to change.
          if (doseMgEl && hrtProfile.testMg && !doseMgEl.value) doseMgEl.value = hrtProfile.testMg;
          activeQuestions = hrtActiveQuestions();
          TOTAL_STEPS = activeQuestions.length + 2;
        })
        .catch(function () { /* offline: fall back to the shared question set */ });
    }

    if (backBtn) backBtn.addEventListener("click", function () {
      if (step > 0) { step -= 1; render(); }
    });

    qsa('[data-wl-open-modal="checkin"]').forEach(function (btn) {
      btn.addEventListener("click", function () {
        reset();
        hrtMaybeShowIntakeGate();
      });
    });

    function showThanks(flagged, flagList, bpEval) {
      surveyEl.style.display = "none";
      thanksEl.style.display = "block";
      const title = qs("[data-wl-checkin-thanks-title]", modal);
      const body = qs("[data-wl-checkin-thanks-body]", modal);
      const cta = qs("[data-wl-checkin-thanks-cta]", modal);
      if (flagged) {
        if (title) title.textContent = "Thank you — we're on it.";
        if (body) body.innerHTML = "It sounds like things could be going better, and we take that seriously. <strong>A member of our team will personally reach out soon.</strong> If you'd rather not wait, call or chat with us right now — we'd love to talk it through.";
        if (cta) cta.style.display = "flex";
      } else {
        if (title) title.textContent = "Thank you! Keep it up.";
        if (body) body.innerHTML = "We love seeing your progress. Your answers help us keep your plan dialed in — see you next week. In the meantime, the chat button is always here if anything comes up.";
        if (cta) cta.style.display = "none";
      }
      // Sexual-health follow-up (Derek 2026-08-29: "if they report negative
      // for that particular question, I can pitch them different medications").
      // Deliberately NOT a hard product ad on a sensitive topic — it names that
      // treatable options exist, opens a private conversation, and points at the
      // sexual-health shelf. The actual clinical pitch happens staff-side off
      // the flag that just went to GHL/Klaviyo.
      const sexEl = qs("[data-wl-sexual-followup]", modal);
      if (sexEl) {
        const sexFlagged = (flagList || []).some(function (f) {
          return f === "low sex drive" || f === "sexual function concern";
        });
        if (sexFlagged) {
          sexEl.style.display = "block";
          sexEl.innerHTML =
            '<div style="border:1px solid rgba(173,137,82,.5);border-left:4px solid #ad8952;background:#fbf7f1;padding:14px 16px;">' +
              '<div style="font-family:Cormorant Garamond,Georgia,serif;font-weight:500;font-size:17px;color:#122a44;margin-bottom:6px;">This part is treatable too</div>' +
              '<p style="margin:0 0 10px;color:#4b5563;font-size:15px;line-height:1.65;">' +
                'Thanks for answering honestly — most people skip that question. Sex drive and ' +
                'sexual function are two different things, and both respond to treatment. Hormones ' +
                'are only one lever; there are others we can add, and it is a normal, routine ' +
                'conversation for us to have.' +
              '</p>' +
              '<div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;">' +
                '<button type="button" class="button button--primary" data-hrt-open-sexual>See the options</button>' +
                '<button type="button" class="wl-linklike" data-wl-open-chat="1">Talk to a provider privately</button>' +
              '</div>' +
            '</div>';
        } else {
          sexEl.style.display = "none";
          sexEl.innerHTML = "";
        }
      }

      // A crisis-level reading outranks everything else on this screen. The
      // portal does not diagnose and never mentions dose — it says "get seen",
      // which is the only safe instruction a web page can give here.
      const bpEl = qs("[data-hrt-bp-notice]", modal);
      if (bpEl) {
        if (bpEval && bpEval.urgent) {
          bpEl.style.display = "block";
          bpEl.innerHTML =
            '<div style="border:2px solid #b3261e;background:#fdf3f2;padding:14px 16px;">' +
              '<div style="font-weight:900;color:#b3261e;font-size:17px;margin-bottom:6px;">Please get this checked today</div>' +
              '<p style="margin:0 0 10px;color:#3f4a5c;font-size:15px;line-height:1.6;">' +
                bpEval.note + ' Do not wait for your next visit &mdash; call us now, or if you feel ' +
                'chest pain, short of breath, weak on one side, or have a bad headache or vision change, ' +
                '<strong>go to an emergency room</strong>.' +
              '</p>' +
              '<a class="button button--primary" href="tel:+14239002994" style="text-decoration:none;">Call us: (423) 900-2994</a>' +
            '</div>';
        } else if (bpEval && bpEval.note) {
          bpEl.style.display = "block";
          bpEl.innerHTML =
            '<div style="border:1px solid #fdba74;background:#fff7ed;padding:12px 14px;color:#9a3412;font-size:15px;line-height:1.6;">' +
              bpEval.note + ' We have flagged it for our team to look at &mdash; keep logging it each week.' +
            '</div>';
        } else {
          bpEl.style.display = "none";
          bpEl.innerHTML = "";
        }
      }

      const bmiValueEl = qs("[data-wl-checkin-bmi-value]", modal);
      if (bmiValueEl && numbers) bmiValueEl.textContent = numbers.bmi.toFixed(1);
    }

    if (submitBtn) submitBtn.addEventListener("click", function () {
      if (submitting) return;
      submitting = true;
      submitBtn.disabled = true;
      submitBtn.textContent = "Sending…";

      let score = 0;
      const flags = [];
      const answerProps = {};
      let maxScore = 0;
      activeQuestions.forEach(function (q) {
        const opt = q.opts[answers[q.k]];
        if (!opt) return;
        answerProps["Check-in: " + q.k] = opt.label;
        // skip:true ("I'd rather not say") is recorded but scored out of
        // existence — neither its points nor its 3 possible points count.
        if (opt.skip) return;
        score += opt.pts;
        maxScore += 3;
        if (opt.flag) flags.push(opt.flag);
      });
      // BP is evaluated against the history we already have on file, so a
      // gradual climb is caught even when every single reading looks fine.
      const bpEval = numbers
        ? hrtEvaluateBp(numbers.bpSys, numbers.bpDia, wlLastLog || [])
        : { flags: [], urgent: false, note: "" };
      bpEval.flags.forEach(function (f) { flags.push(f); });

      // Escalate on the headache red-flag answer too. hrtEvaluateBp only knows
      // about numbers; this is the symptom that most often accompanies them.
      const headacheOpt = (function () {
        const q = activeQuestions.filter(function (x) { return x.k === "headaches"; })[0];
        return q ? q.opts[answers.headaches] : null;
      })();
      if (headacheOpt && headacheOpt.flag === "SEVERE HEADACHE / VISION CHANGE") {
        bpEval.urgent = true;
        if (!bpEval.note) {
          bpEval.note = "A severe headache — especially with any change in your vision — is worth being seen for today.";
        }
      }

      const pct = Math.round((score / maxScore) * 100);
      const comment = commentEl ? commentEl.value.trim() : "";
      // Alert below "all mostly good" (all 2s). Derived from maxScore so a
      // skipped question can't shift the bar: 30 -> 19, 33 -> 21, matching the
      // old fixed WL_CHECKIN_ALERT_MAX exactly at 10 questions.
      const alertMax = Math.floor(maxScore * 2 / 3) - 1;
      const flagged = score <= alertMax || flags.length > 0;
      const now = new Date().toISOString();

      const props = Object.assign({
        "Score": score,
        "Max Score": maxScore,
        "Score Percent": pct,
        "Flagged": flagged ? "Yes" : "No",
        "Flag Reasons": flags.join("; "),
        "Comment": comment,
        "Completed At": now,
        "Weight (lb)": numbers ? numbers.weightLb : null,
        "BMI": numbers ? numbers.bmi : null,
        "Dose (mg)": numbers && numbers.doseMg !== null ? numbers.doseMg : "",
        "BP Systolic": (numbers && numbers.bpSys) || "",
        "BP Diastolic": (numbers && numbers.bpDia) || "",
        "Testosterone (mg/wk)": (numbers && numbers.testMg) || "",
        "Injection Route": (numbers && numbers.route ? numbers.route.toUpperCase() : ""),
        "Sex": hrtProfile.sex || pendingSex || "",
        "Waist (in)": (numbers && numbers.measurements && numbers.measurements.waistIn) || "",
        "Hips (in)": (numbers && numbers.measurements && numbers.measurements.hipsIn) || "",
        "Arms (in)": (numbers && numbers.measurements && numbers.measurements.armsIn) || "",
        "Legs (in)": (numbers && numbers.measurements && numbers.measurements.legsIn) || ""
      }, answerProps);

      // Mirror the weigh-in into the local BMI log (overwrite same-day entry,
      // same rule the old standalone tracker used) so the graph has an
      // offline fallback.
      const entryDate = todayISO();
      if (numbers) {
        const entries = storageLoadBmiLog();
        const entry = { date: entryDate, weight_lb: numbers.weightLb, bmi: numbers.bmi };
        const existingIdx = entries.findIndex(function (e) { return e.date === entryDate; });
        if (existingIdx >= 0) entries[existingIdx] = entry; else entries.push(entry);
        storageSaveBmiLog(entries);
      }

      const done = [];

      // Server persistence — same endpoints the standalone BMI tracker and
      // hero dose card used (customer note + metafield via the app proxy).
      let serverLog = null;
      if (numbers) {
        done.push(
          fetch("/apps/wl-hub/bmi", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ date: entryDate, weightLb: numbers.weightLb, bmi: numbers.bmi, measurements: numbers.measurements })
          }).then(function (res) { return res.json().catch(function () { return {}; }).then(function (data) {
            if (res.ok && data.ok && data.log) serverLog = data.log;
            if (data && data.goal !== undefined && data.goal) { wlGoalWeight = data.goal; wlGoalKnown = true; }
          }); }).catch(function (err) { console.error("[WL] Check-in BMI save failed:", err); })
        );
        if (numbers.doseMg !== null) {
          done.push(
            fetch("/apps/wl-hub/dose", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ dose: numbers.doseMg })
            }).catch(function (err) { console.error("[WL] Check-in dose save failed:", err); })
          );
        }
      }

      // Locked goal weight — set-once; the server refuses any second write.
      if (pendingGoalLb !== null) {
        done.push(
          fetch("/apps/wl-hub/goal", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ goalLb: pendingGoalLb })
          }).then(function (res) { return res.json().catch(function () { return {}; }).then(function (data) {
            if (data.goal) { wlGoalWeight = data.goal; wlGoalKnown = true; }
          }); }).catch(function (err) { console.error("[WL] Goal save failed:", err); })
        );
      }

      // Sex (set-once) + dose/route tags. Fire-and-forget like the rest.
      if (pendingSex || numbers?.testMg || numbers?.route) {
        done.push(
          fetch("/apps/wl-hub/hrt", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sex: pendingSex || undefined,
              testMg: numbers && numbers.testMg !== null ? numbers.testMg : undefined,
              route: numbers && numbers.route ? numbers.route : undefined
            })
          }).then(function (res) { return res.json().catch(function () { return {}; }); })
            .then(function (data) { if (data && data.sex) hrtProfile.sex = data.sex; })
            .catch(function (err) { console.error("[HRT] profile save failed:", err); })
        );
      }

      if (customer.email) done.push(wlCheckinKlaviyo(customer.email, props));
      if (flagged) {
        // Pre-formatted alert body, same reason as the WL portal: GHL's
        // notification editor drops labels and line breaks, so the readable
        // message is built HERE and dropped into the email as ONE merge field.
        // `summary` uses <br> (GHL renders the body as HTML); `summary_text`
        // is the plain-newline twin.
        //
        // ⚠️ THIS IS NOT OPTIONAL. The GHL notification body is essentially
        // just {{inboundWebhookRequest.summary}} - a duplicate of the WL
        // workflow pointed at a payload without this key sends an email with
        // the member's name and NOTHING ELSE. Verified 2026-08-31 before the
        // HRT webhook went live.
        //
        // No MACROS line: that is weight-loss only and wlMacros does not exist
        // in this portal.
        const who = ((customer.first_name || "") + " " + (customer.last_name || "")).trim() || "A member";
        // The summary is the ONLY thing the email shows, so every field the
        // hormone check-in collects has to appear here or it is invisible to
        // staff. Ported-from-WL versions of this list lacked BP / dose / route
        // / sex, which meant a blood-pressure alert arrived without the blood
        // pressure in it (fixed 2026-09-01 after Derek read a live one).
        const bpLine = numbers && numbers.bpSys
          ? numbers.bpSys + "/" + numbers.bpDia + (bpEval.urgent ? "   *** URGENT ***" : "")
          : "not logged";
        const tLine = numbers && numbers.testMg
          ? numbers.testMg + " mg/week" + (numbers.route ? " " + numbers.route.toUpperCase() : "")
          : "not logged this week";

        const lines = [];
        // Urgency goes FIRST — a crisis BP or a severe headache should be
        // readable in the notification preview without opening the email.
        if (bpEval.urgent) {
          lines.push("*** URGENT — NEEDS A CALL TODAY ***");
        }
        lines.push(
          "FLAGGED: " + who + "  —  " + score + "/" + maxScore + " (" + pct + "%)",
          "WHY: " + (flags.join("; ") || "low overall score"),
          "THEY SAID: " + (comment || "(no comment left)"),
          "BLOOD PRESSURE: " + bpLine,
          "TESTOSTERONE: " + tLine,
          "NUMBERS: " + (numbers && numbers.weightLb
            ? numbers.weightLb + " lb · BMI " + numbers.bmi
            : "not logged") +
            (numbers && numbers.doseMg !== null && numbers.doseMg !== undefined
              ? " · dose " + numbers.doseMg + " mg" : ""),
          "ON FILE: " + ((hrtProfile.sex || pendingSex || "sex not set")) +
            (wlGoalWeight && wlGoalWeight.goalLb ? " · goal " + wlGoalWeight.goalLb + " lb" : ""),
          "ANSWERS: " + Object.keys(answerProps).map(function (k) {
            return k.replace("Check-in: ", "") + " = " + answerProps[k];
          }).join(" | "),
          "PHONE: " + (customer.phone || "(none on file)"),
          "EMAIL: " + (customer.email || "(none on file)")
        );
        const summaryText = lines.join(String.fromCharCode(10));
        const summaryHtml = lines.join("<br>");

        done.push(wlCheckinAlert(cfg.webhook, {
          type: "hrt_checkin_alert",
          summary: summaryHtml,
          summary_text: summaryText,
          email: customer.email || "",
          first_name: customer.first_name || "",
          last_name: customer.last_name || "",
          phone: customer.phone || "",
          shopify_customer_id: customer.id || "",
          score: score,
          max_score: maxScore,
          score_percent: pct,
          flag_reasons: flags.join("; ") || "low overall score",
          comment: comment,
          weight_lb: numbers ? numbers.weightLb : "",
          bmi: numbers ? numbers.bmi : "",
          dose_mg: numbers && numbers.doseMg !== null ? numbers.doseMg : "",
          bp: numbers && numbers.bpSys ? numbers.bpSys + "/" + numbers.bpDia : "",
          bp_urgent: bpEval.urgent ? "YES" : "",
          testosterone_mg_per_week: numbers && numbers.testMg ? numbers.testMg : "",
          injection_route: numbers && numbers.route ? numbers.route.toUpperCase() : "",
          sex: hrtProfile.sex || pendingSex || "",
          answers: answerProps,
          completed_at: now,
          portal_url: location.href.split("?")[0]
        }));
      }

      try { localStorage.setItem(WL_CHECKIN_KEY, now); } catch (_) {}

      function finish() {
        if (!submitting) return;
        submitting = false;
        wlCheckinRenderCardState();
        showThanks(flagged, flags, bpEval);
        // Graph: prefer the fresh server log from the POST; otherwise fetch
        // it (wlLoadBmiGraph falls back to its own error copy), and if the
        // server is unreachable render the local log so the member still
        // sees their trend.
        if (serverLog) {
          const statusEl2 = qs("[data-wl-bmi-graph-status]");
          if (statusEl2) statusEl2.textContent = "";
          wlRenderBmiChart(serverLog);
          wlCheckBmiRate(serverLog);
          wlRenderGoalProgress(serverLog);
          wlRenderInchesSummary(serverLog);
        } else {
          wlLoadBmiGraph().then(function () {
            const container = qs("[data-wl-bmi-graph-container]");
            if (container && !container.querySelector("svg")) {
              const local = storageLoadBmiLog().map(function (e) { return { date: e.date, weightLb: e.weight_lb }; });
              if (local.length >= 2) {
                const statusEl2 = qs("[data-wl-bmi-graph-status]");
                if (statusEl2) statusEl2.textContent = "Showing entries saved on this device.";
                wlRenderBmiChart(local);
                wlCheckBmiRate(local);
              }
            }
          });
        }
      }

      // Never trap the member on a spinner — sends are fire-and-forget with
      // a short grace so the requests get out before any navigation.
      Promise.allSettled(done).finally(finish);
      setTimeout(finish, 3500);
    });

    render();
  }

  // -----------------------------
  // Intake-form gate (Derek 2026-08-29)
  //
  // The HRT intake & consent form moved to a digital copy in GHL. Two surfaces:
  //   1. a dismissible card on the portal page (#hrt-intake)
  //   2. a one-time panel shown INSTEAD of the survey the first time someone
  //      opens the weekly check-in
  // Both disappear once the member opens the form or says they already did it.
  //
  // Storage is localStorage — per-browser, and only a NUDGE. The authoritative
  // record of who has actually completed intake is the submission in GHL; this
  // must never be presented to staff or the patient as proof of completion.
  // "Skip for now" deliberately does NOT persist, so it asks again next week.
  // -----------------------------
  const HRT_INTAKE_KEY = "hrt_intake_ack_v1";

  // Server value wins; localStorage is only a fast mirror so the card does not
  // flash before /apps/wl-hub/hrt answers. Set by refreshHrtProfile().
  let hrtIntakeAckServer = null;

  function hrtIntakeAcked() {
    if (hrtIntakeAckServer) return true;
    try { return localStorage.getItem(HRT_INTAKE_KEY) === "1"; } catch (_) { return false; }
  }
  function hrtIntakeAck() {
    try { localStorage.setItem(HRT_INTAKE_KEY, "1"); } catch (_) {}
    hrtIntakeAckServer = hrtIntakeAckServer || "pending";
    const card = qs("[data-hrt-intake-card]");
    if (card) card.style.display = "none";
    hrtHideIntakeGate();
    // Persist so it follows the patient to their phone / home-screen app.
    fetch("/apps/wl-hub/hrt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ intakeAck: true })
    }).then(function (r) { return r.json(); })
      .then(function (d) { if (d && d.intakeAck) hrtIntakeAckServer = d.intakeAck; })
      .catch(function () { /* localStorage still covers this device */ });
  }
  function hrtHideIntakeGate() {
    const gate = qs("[data-hrt-intake-gate]");
    const survey = qs("[data-wl-checkin-survey]");
    if (gate) gate.style.display = "none";
    if (survey) survey.style.display = "block";
  }
  // Called when the check-in modal opens: show the gate instead of the survey
  // if intake hasn't been acknowledged yet.
  function hrtMaybeShowIntakeGate() {
    const gate = qs("[data-hrt-intake-gate]");
    const survey = qs("[data-wl-checkin-survey]");
    if (!gate || !survey) return;
    const thanks = qs("[data-wl-checkin-thanks]");
    if (hrtIntakeAcked()) { gate.style.display = "none"; survey.style.display = "block"; return; }
    gate.style.display = "block";
    survey.style.display = "none";
    if (thanks) thanks.style.display = "none";
  }

  function initIntakeGate() {
    const card = qs("[data-hrt-intake-card]");
    if (card && !hrtIntakeAcked()) card.style.display = "block";

    // Ask the server on load too — otherwise the card sits visible until the
    // member happens to open the check-in.
    fetch("/apps/wl-hub/hrt")
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d && d.ok && d.intakeAck) {
          hrtIntakeAckServer = d.intakeAck;
          try { localStorage.setItem(HRT_INTAKE_KEY, "1"); } catch (_) {}
          if (card) card.style.display = "none";
        }
      })
      .catch(function () {});

    document.addEventListener("click", function (e) {
      // Opening the form counts as handled — it opens in a new tab, so the
      // member keeps their place in the portal behind it.
      if (e.target.closest("[data-hrt-intake-open]")) { hrtIntakeAck(); return; }
      if (e.target.closest("[data-hrt-intake-done]")) { hrtIntakeAck(); return; }
      // Skip: reveal the survey for THIS visit only, don't remember it.
      if (e.target.closest("[data-hrt-intake-skip]")) { hrtHideIntakeGate(); return; }
    });
  }

  // -----------------------------
  // "Add to home screen" prompt (Derek 2026-08-30)
  //
  // Shows once per device, on phones only, and never when the portal is ALREADY
  // running standalone (that check is what stops it nagging the very people who
  // took the advice). theme.liquid already carries the apple-mobile-web-app-*
  // tags for these two templates, so on iOS the result is a genuine full-screen
  // app rather than a bookmark.
  //
  // There is no web app manifest, so Android's native beforeinstallprompt never
  // fires — Android users get menu instructions instead. Adding a manifest would
  // make the install banner automatic, but it is a theme-wide file, so it stayed
  // out of scope here.
  // -----------------------------
  const HRT_INSTALL_KEY = "hrt_install_prompt_v1";

  function hrtIsStandalone() {
    try {
      if (window.navigator.standalone === true) return true;            // iOS
      return !!(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
    } catch (_) { return false; }
  }

  function hrtIsPhone() {
    const ua = navigator.userAgent || "";
    return /iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua) ||
           (/iPad|Android/i.test(ua) && window.innerWidth <= 1024);
  }

  function hrtInstallSteps() {
    const ua = navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/i.test(ua);
    if (ios) {
      return "1. Tap the <strong>Share</strong> button at the bottom of Safari " +
             "(the square with an arrow pointing up).<br>" +
             "2. Scroll down and tap <strong>Add to Home Screen</strong>.<br>" +
             "3. Tap <strong>Add</strong>. Done — look for the Reflections icon.";
    }
    return "1. Tap the <strong>⋮ menu</strong> in the top-right of Chrome.<br>" +
           "2. Tap <strong>Add to Home screen</strong> (some phones call it <strong>Install app</strong>).<br>" +
           "3. Confirm. Done — look for the Reflections icon.";
  }

  function initInstallPrompt() {
    const modal = qs('[data-wl-modal="install-app"]');
    if (!modal) return;
    if (hrtIsStandalone()) return;                 // already installed
    if (!hrtIsPhone()) return;                     // desktop: not useful
    try { if (localStorage.getItem(HRT_INSTALL_KEY) === "1") return; } catch (_) {}

    const steps = qs("[data-hrt-install-steps]", modal);
    if (steps) steps.innerHTML = hrtInstallSteps();

    // Dismissing (any close control) is permanent — this is a convenience tip,
    // not something to ask twice.
    qsa("[data-wl-modal-close]", modal).forEach(function (btn) {
      btn.addEventListener("click", function () {
        try { localStorage.setItem(HRT_INSTALL_KEY, "1"); } catch (_) {}
      });
    });

    // Let the page settle, and never stack on top of another open modal
    // (the intake gate and check-in both matter more than this tip).
    setTimeout(function () {
      if (qsa('[data-wl-modal][aria-hidden="false"]').length) return;
      openModalByName("install-app");
    }, 2200);
  }

  // -----------------------------
  // Lab uploads (Derek 2026-09-01)
  //
  // The card is hidden in the markup and only revealed once the server confirms
  // Dropbox is configured, so this could ship before the credentials existed
  // without ever showing a button that throws.
  //
  // PROGRAM is hard-coded per portal — this file is the hormone build, the
  // weight-loss fork passes "wl". The server maps it to the Dropbox folder and
  // rejects anything else.
  // -----------------------------
  const HRT_LABS_PROGRAM = "hrt";
  let hrtLabFiles = [];
  let hrtLabsLimits = { maxFiles: 8, maxBytes: 25 * 1024 * 1024 };

  function hrtLabsFmtSize(bytes) {
    if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + " MB";
    return Math.max(1, Math.round(bytes / 1024)) + " KB";
  }

  function hrtLabsRender() {
    const list = qs("[data-hrt-labs-list]");
    const send = qs("[data-hrt-labs-send]");
    if (!list || !send) return;

    if (!hrtLabFiles.length) {
      list.style.display = "none";
      list.innerHTML = "";
      send.disabled = true;
      send.style.opacity = ".45";
      send.style.cursor = "not-allowed";
      send.textContent = "Send my labs";
      return;
    }

    list.style.display = "block";
    list.innerHTML = hrtLabFiles.map(function (f, i) {
      const over = f.size > hrtLabsLimits.maxBytes;
      return '<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;' +
             'padding:9px 12px;border:1px solid ' + (over ? "#f2b8b5" : "#e5e7eb") + ';' +
             'background:' + (over ? "#fdf3f2" : "#fff") + ';margin-bottom:6px;">' +
               '<span style="font-size:15px;color:#122a44;word-break:break-all;">' + f.name + '</span>' +
               '<span style="font-size:15px;color:' + (over ? "#b3261e" : "#8a8f99") + ';white-space:nowrap;">' +
                 hrtLabsFmtSize(f.size) + (over ? " — too big" : "") +
                 ' <button type="button" data-hrt-labs-remove="' + i + '" aria-label="Remove" ' +
                 'style="border:0;background:none;color:#8a8f99;font-size:19px;line-height:1;cursor:pointer;padding:0 0 0 8px;">&times;</button>' +
               '</span>' +
             '</div>';
    }).join("");

    const sendable = hrtLabFiles.filter(function (f) { return f.size <= hrtLabsLimits.maxBytes; });
    send.disabled = sendable.length === 0;
    send.style.opacity = sendable.length ? "1" : ".45";
    send.style.cursor = sendable.length ? "pointer" : "not-allowed";
    send.textContent = sendable.length === 1 ? "Send 1 file" : "Send " + sendable.length + " files";
  }

  function initLabUploads() {
    const card = qs("[data-hrt-labs-card]");
    if (!card) return;
    const input = qs("[data-hrt-labs-input]");
    const statusEl = qs("[data-hrt-labs-status]");

    // Only reveal the card once the server says uploads actually work.
    fetch("/apps/wl-hub/labs")
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.ok || !d.configured) return;
        hrtLabsLimits = { maxFiles: d.maxFiles || 8, maxBytes: d.maxBytes || hrtLabsLimits.maxBytes };
        card.style.display = "block";
        if (input && Array.isArray(d.accept)) {
          input.setAttribute("accept", d.accept.map(function (e) { return "." + e; }).join(","));
        }
      })
      .catch(function () { /* leave hidden */ });

    if (input) {
      input.addEventListener("change", function () {
        const picked = Array.prototype.slice.call(input.files || []);
        hrtLabFiles = hrtLabFiles.concat(picked).slice(0, hrtLabsLimits.maxFiles);
        input.value = "";           // so re-picking the same file still fires change
        if (statusEl) statusEl.textContent = "";
        hrtLabsRender();
      });
    }

    document.addEventListener("click", function (e) {
      const rm = e.target.closest("[data-hrt-labs-remove]");
      if (rm) {
        hrtLabFiles.splice(parseInt(rm.getAttribute("data-hrt-labs-remove"), 10), 1);
        hrtLabsRender();
        return;
      }

      const send = e.target.closest("[data-hrt-labs-send]");
      if (!send || send.disabled) return;

      const files = hrtLabFiles.filter(function (f) { return f.size <= hrtLabsLimits.maxBytes; });
      if (!files.length) return;

      const fd = new FormData();
      fd.append("program", HRT_LABS_PROGRAM);
      files.forEach(function (f) { fd.append("files", f, f.name); });

      send.disabled = true;
      send.style.opacity = ".45";
      send.textContent = "Sending…";
      if (statusEl) statusEl.textContent = "Uploading — please keep this page open.";

      fetch("/apps/wl-hub/labs", { method: "POST", body: fd })
        .then(function (r) { return r.json().catch(function () { return {}; }); })
        .then(function (d) {
          const okCount = (d.uploaded || []).length;
          const bad = d.rejected || [];
          if (okCount) {
            // Clear only what actually landed, so a partial failure leaves the
            // failed files staged for a retry instead of silently vanishing.
            // Keep ONLY the files the server rejected, so a partial failure
            // leaves those staged for a retry while the successful ones clear.
            // Matched on the rejected list because the server returns SAFE
            // (sanitised) names for successes, which need not equal f.name.
            hrtLabFiles = hrtLabFiles.filter(function (f) {
              return bad.some(function (b) { return b.fileName === f.name; });
            });
          }
          hrtLabsRender();
          if (statusEl) {
            let msg = "";
            if (okCount) {
              msg = "<strong style=\"color:#1e7a4d;\">Got them — " + okCount + " file" +
                    (okCount > 1 ? "s" : "") + " sent to your care team.</strong>";
            }
            if (bad.length) {
              msg += (msg ? "<br>" : "") + bad.map(function (b) {
                return "<span style=\"color:#b45309;\">" + b.fileName + " — " + b.reason + "</span>";
              }).join("<br>");
            }
            if (!okCount && !bad.length) {
              msg = "<span style=\"color:#b45309;\">" + (d.error || "That didn't go through — please try again, or send them in chat.") + "</span>";
            }
            statusEl.innerHTML = msg;
          }
        })
        .catch(function () {
          if (statusEl) statusEl.innerHTML = '<span style="color:#b45309;">That did not go through &mdash; please try again, or send them in chat.</span>';
          hrtLabsRender();
        });
    });
  }

  // -----------------------------
  // Boot
  // -----------------------------
  document.addEventListener("DOMContentLoaded", function () {
    initMotivation();
    initIntakeGate();
    initLabUploads();
    initInstallPrompt();
    initCheckin();
    initModals();
    initBmi();
    initBmr();
    initMacros();
    initDose();
    initDoseCalculator();
    initAddons();
    initPeptideQuiz();
    initRivoRewards();
    initTelemedCompliance();

    // If any modals are open by default (unlikely), render bmi log + macro calories
    renderBmiLog();
    refreshMacroCaloriesUI();
  });

  // Chat click binding lives here deliberately — a synchronous top-level
  // statement at the very end of the IIFE, NOT inside the DOMContentLoaded
  // block above. Matches the working pattern from
  // wl-post-visit-checkout.js exactly (its chat listener is also a
  // top-level statement positioned right before that file's own EXPORTS
  // section, at the end of its IIFE) — confirmed by Derek as the detail
  // that made this work on that page.
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-wl-open-chat]");
    if (!btn) return;
    e.preventDefault();

    if (wlOpenGhlChat()) return;

    const originalText = btn.textContent;
    btn.textContent = "Opening chat…";

    let attemptsLeft = 40;
    const retry = setInterval(() => {
      if (wlOpenGhlChat() || attemptsLeft <= 0) {
        clearInterval(retry);
        btn.textContent = originalText;
        if (attemptsLeft <= 0) {
          console.warn("[WL] Chat widget never finished loading — please try again.");
        }
        return;
      }
      attemptsLeft -= 1;
    }, 500);
  });
})();



